import { useState, useRef, useEffect, useLayoutEffect, useCallback, useMemo } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { User, Settings, Plus, Navigation, Palette, Undo2, Redo2, MapPinOff, Trash2, AlertTriangle, MapPinned, CloudSun } from 'lucide-react';
import { MapContainer, MapContainerRef } from './components/map/MapContainer';
import { MAP_DEFAULT_ZOOM, MAP_FOCUS_ZOOM } from './components/map/config/mapConfig';
import { MapControlsFAB, MapStyle } from './components/map/MapControlsFAB';
import { LayerSettingsPanel, LayerSettings, DEFAULT_LAYER_SETTINGS, type LayerSectionId } from './components/map/LayerSettingsPanel';
import {
  OBSERVED_WEATHER_LAYER_KEYS,
  OBSERVED_WEATHER_UI_DEFS,
  OBSERVED_WEATHER_TILE_MIN_ZOOM,
  type ObservedWeatherLayerKey,
  type ObservedWeatherPeriodKey,
} from './lib/observedWeather';
import {
  FORECAST_WEATHER_LAYER_KEYS,
  type ForecastWeatherPeriodKey,
} from './lib/forecastWeather';
import { fetchTiledPeriodManifest } from './lib/tilePeriods';
import { resolveOutlookPeriods } from './lib/forecastOutlook';
import { WeatherOutlookPanel } from './components/weather/WeatherOutlookPanel';
import { WeatherSummaryModal } from './components/weather/WeatherSummaryModal';
import { WeatherDailyBriefing } from './components/weather/WeatherDailyBriefing';
import { WeatherSubscribeModal } from './components/weather/WeatherSubscribeModal';
import { buildForecastSummary } from './lib/weatherForecastSummary';
import { isWeatherDailyBriefingDismissed, markWeatherDailyBriefingDismissed } from './lib/weatherDailyBriefingStorage';
import { useForecastAtPoint } from './components/weather/useForecastAtPoint';
import { useWeatherSubscription } from './components/weather/useWeatherSubscription';
import {
  useLiveForecastLocation,
  useRequestLocationForWeather,
} from './components/weather/useLiveForecastLocation';
import { MapTopRightOverlays } from './components/map/MapTopRightOverlays';
import type { ForecastVariable } from './components/weather/WeatherDashboard';
import { nextPitchStep } from './utils/mapPitchCycle';
import {
    adminPlaceFitMaxZoom,
    isAdminBoundaryPolygon,
    isAdminPointGeometry,
} from './utils/adminBoundaryFocus';
import { isAdminPlaceType } from './utils/contributionPlaceAdmin';
import { MapAttribution } from './components/map/MapAttribution';
import { SearchBar } from './components/search/SearchBar';
import { BottomSheet } from './components/ui/BottomSheet';
import { ExpandableFAB } from './components/ui/ExpandableFAB';
import { PlaceDetails } from './components/places/PlaceDetails';
import { HomeContent } from './components/places/HomeContent';
import { AddPlaceModal } from './components/places/AddPlaceModal';
import { DirectionsPanel } from './components/directions/DirectionsPanel';
import { NavigationView } from './components/directions/NavigationView';
import { AuthModal } from './components/auth/AuthModal';
import { EmailVerification } from './components/auth/EmailVerification';
import { ResetPassword } from './components/auth/ResetPassword';
import { ProfilePanel } from './components/profile/ProfilePanel';
import { SettingsPanel, type SettingsPanelSection } from './components/settings/SettingsPanel';
import { AddLocationPanel } from './components/contributions/AddLocationPanel';
import { MyContributionsPanel } from './components/contributions/MyContributionsPanel';
import { type ContributionDetail, getContribution, fetchMyCampaignAssignments, fetchMyAssignedZones, fetchMyActiveCampaignSession, APIError } from './services/api';
import type { Place, GeoJSONGeometry } from './types';
import { contributionDetailToPreviewPlace, isContributionPreviewPlaceId } from './utils/contributionPreviewPlace';
import {
    collectContributionGeometries,
    contributionDeepLinkCoords,
    contributionMapBoundsGeometry,
    getDividedCarriagewayGuides,
    parseStoredGeometry,
} from './utils/contributionGeometry';
import {
    clearContributionDeepLinkId,
    readContributionDeepLinkCoords,
    readContributionDeepLinkId,
    stashContributionDeepLinkId,
    stripContributionDeepLinkParams,
    writeContributionDeepLinkToUrl,
} from './utils/contributionDeepLink';
import { SavedPlacesPanel } from './components/profile/SavedPlacesPanel';
import { FindPostcodePanel, type FindPostcodeSnapshot } from './components/postcode/FindPostcodePanel';
import { RecentActivityPanel } from './components/profile/RecentActivityPanel';
import { InstallPrompt } from './components/pwa/InstallPrompt';
import { LocationPermissionPrompt } from './components/pwa/LocationPermissionPrompt';
import { ProductTour } from './components/tour/ProductTour';
// CookieConsent removed — now only on marketing site
import MapSplashLoader from './components/ui/MapSplashLoader';
import { FeedbackSurvey } from './components/ui/FeedbackSurvey';
import { useFeedbackSurvey } from './hooks/useFeedbackSurvey';
import { useContributionNotifications } from './hooks/useContributionNotifications';
import { ContributionNotificationToast } from './components/ui/ContributionNotificationToast';
import { CampaignWorkspacePanel } from './components/campaign/CampaignWorkspacePanel';
import { MyCampaignsLauncher } from './components/campaign/MyCampaignsLauncher';
import { useMapStore } from './stores/mapStore';
import { useAuthStore } from './stores/authStore';
import { useDirectionsStore } from './stores/directionsStore';
import { useSettingsStore } from './stores/settingsStore';
import { applyPreferredTravelModeFromSettings } from './lib/navigationPreferences';
import { useTourStore } from './stores/tourStore';
import { useTheme } from './hooks/useTheme';
import { addressIncludesPostcode } from './utils/postcode';
import { isSessionStatusActive } from './utils/authUserId';
import { mapPositionToCoordinates } from './utils/geolocation';
import {
  DEFAULT_TRACE_MIN_DISTANCE_M,
  isGpsAccuracyAcceptableForFieldMapping,
  shouldAcceptTracePoint,
  type TraceCaptureMode,
  type TraceDistanceOptionM,
  type TraceSamplingMode,
} from './utils/fieldMappingGps';
import { requestCurrentPosition, waitForFieldMappingPosition } from './utils/geolocationRequest';
import { registerLocationPromptOpener, openLocationPrompt } from './utils/locationPrompt';
import { canStartLiveNavigation, canShowWeatherForecast, weatherForecastUnavailableMessage } from './utils/regionAvailability';
import { IS_PLAYWRIGHT_TEST } from './lib/env';
import {
    readFieldCampaignsExploreEngagedFromSession,
    setFieldCampaignsExploreEngagedInSession,
    clearFieldCampaignsExploreEngagedFromSession,
    readStoredCampaignWorkspaceResumeId,
    readStoredCampaignWorkspaceResumeZoneId,
    setCampaignWorkspaceResumeIdInSession,
    clearCampaignWorkspaceResumeIdFromSession,
    readCampaignHomeOverlayFromSession,
    setCampaignHomeOverlayInSession,
} from './utils/fieldCampaignsExploreSession';
import {
    clearFieldTraceDraft,
    readFieldTraceDraft,
    writeFieldTraceDraft,
    type FieldTraceDraft,
} from './utils/fieldTraceDraftStorage';
import {
    pickFinerSpacingM,
    pickThinSpacingM,
    postProcessTracePath,
    removeTraceOutliers,
    resampleFinerTracePath,
    resampleTracePathIfGap,
    straightenTracePath,
    thinTracePath,
} from './utils/tracePostProcess';
import { traceProducesPolygon } from './utils/contributionGeometry';
import {
    snapLineStringEndpoints,
    tryClosePolygonRing,
    type SnapConnectionRecord,
} from './features/geometry-editing/roadEndpointSnap';
import { roadSnapBandsFromSettings } from './features/geometry-editing/roadSnapBands';
import { usePendingSurveyGeometries } from './hooks/usePendingSurveyGeometries';
import './index.css';

function App() {
    const {
        selectedPlace,
        isMapLoaded,
        userLocation,
        setUserLocation,
        flyToLocation,
        setSheetState,
        selectPlace,
        setSelectedPlaceFromData,
        clearSelectedPlace,
    } = useMapStore();
    const { isAuthenticated, requireAuth, handleOAuthCallback, user, openAuthModal, showAuthModal } = useAuthStore();
    const { origin, destination, routes } = useDirectionsStore();
    const { settings, updateSetting } = useSettingsStore();
    const roadSnapBands = useMemo(
        () => roadSnapBandsFromSettings(settings),
        [settings.roadSnapAutoRadiusM, settings.roadSnapSearchRadiusM],
    );
    const { startTour, startCampaignTour, hasCompleted, hasCompletedCampaign, isActive: tourIsActive } = useTourStore();
    const { survey, triggerSurvey, cancelPending, dismissSurvey, submitSurvey } = useFeedbackSurvey();
    const mapRef = useRef<MapContainerRef>(null);
    const hasAutoLocated = useRef(false);
    const hasCheckedOAuth = useRef(false);
    const prevSelectedPlaceId = useRef<string | null>(null);
    const [viewingContribution, setViewingContribution] = useState<ContributionDetail | null>(null);


    const [urlCampaignId, setUrlCampaignId] = useState<string | null>(() => {
        const fromUrl = new URLSearchParams(window.location.search).get('campaign_id');
        if (fromUrl) return fromUrl;
        return readStoredCampaignWorkspaceResumeId();
    });
    const roadSnapFetchOptions = useMemo(
        () => ({
            includeMyPending: true as const,
            campaignId: urlCampaignId,
        }),
        [urlCampaignId],
    );
    const [pendingSurveyRefreshNonce, setPendingSurveyRefreshNonce] = useState(0);
    const [urlZoneId, setUrlZoneId] = useState<string | null>(() => {
        const fromUrl = new URLSearchParams(window.location.search).get('zone_id');
        if (fromUrl) return fromUrl;
        return readStoredCampaignWorkspaceResumeZoneId();
    });
    const [campaignPanelCollapsed, setCampaignPanelCollapsed] = useState(false);
    /** Field capture opened from campaign session — seeds live GPS into contribution flow. */
    const [campaignFieldCapture, setCampaignFieldCapture] = useState(false);
    /** Explore home chrome while campaign session stays active (`?campaign_id=` retained). */
    const [campaignHomeOverlay, setCampaignHomeOverlay] = useState(() => {
        const fromUrl = new URLSearchParams(window.location.search).get('campaign_id');
        const resumeId = readStoredCampaignWorkspaceResumeId();
        if (fromUrl || resumeId) return readCampaignHomeOverlayFromSession();
        return false;
    });
    /** While true, map and URL must not switch zones (session bound to current zone). */
    const [fieldSessionActive, setFieldSessionActive] = useState(false);
    /** While true, live GPS capture may link submissions to the active campaign session. */
    const [fieldCaptureLive, setFieldCaptureLive] = useState(false);

    useEffect(() => {
        registerLocationPromptOpener(() => setShowLocationPrompt(true));
        return () => registerLocationPromptOpener(null);
    }, []);

    // Trigger place lookup survey 3s after a new place is selected
    useEffect(() => {
        const id = selectedPlace?.id ?? null;
        if (id && id !== prevSelectedPlaceId.current) {
            prevSelectedPlaceId.current = id;
            if (!isContributionPreviewPlaceId(id)) {
                cancelPending();
                triggerSurvey('place_lookup', selectedPlace?.name, 3000);
            }
        } else if (!id) {
            prevSelectedPlaceId.current = null;
            cancelPending();
        }
    }, [selectedPlace?.id]);

    // Admin place: highlight boundary polygon or centroid point and focus the map.
    useEffect(() => {
        if (!isMapLoaded || !selectedPlace) {
            if (!viewingContribution) {
                mapRef.current?.clearHighlight();
            }
            return;
        }

        if (isContributionPreviewPlaceId(selectedPlace.id)) {
            return;
        }

        if (!isAdminPlaceType(selectedPlace.placeType)) {
            mapRef.current?.clearHighlight();
            return;
        }

        const geom = selectedPlace.geometry;
        if (!geom) {
            mapRef.current?.clearHighlight();
            return;
        }

        const maxZoom = adminPlaceFitMaxZoom(selectedPlace.category, selectedPlace.adminLevel);
        mapRef.current?.highlightGeometry(geom as GeoJSON.Geometry);

        if (isAdminBoundaryPolygon(geom)) {
            mapRef.current?.fitCampaignGeometry(geom as GeoJSON.Geometry, {
                maxZoom,
                padding: 56,
            });
        } else if (isAdminPointGeometry(geom)) {
            const [lng, lat] = geom.coordinates as [number, number];
            mapRef.current?.flyTo({ lng, lat }, maxZoom);
        }
    }, [
        isMapLoaded,
        viewingContribution,
        selectedPlace?.id,
        selectedPlace?.placeType,
        selectedPlace?.category,
        selectedPlace?.adminLevel,
        selectedPlace?.geometry,
    ]);

    // Check for hero mode (embedded in marketing site - disables geolocation prompts)
    const isHeroMode = new URLSearchParams(window.location.search).get('mode') === 'hero';

    // Check for email verification route
    const urlParams = new URLSearchParams(window.location.search);
    const verificationToken = window.location.pathname === '/verify-email' ? urlParams.get('token') : null;

    // Check for password reset route
    const resetToken = window.location.pathname === '/reset-password' ? urlParams.get('token') : null;

    // Apply theme from settings
    useTheme();

    useEffect(() => {
        void applyPreferredTravelModeFromSettings(settings, { refetch: false });
    }, [settings.preferredMode]);

    useEffect(() => {
        const onPop = () => {
            const p = new URLSearchParams(window.location.search);
            setUrlCampaignId(p.get('campaign_id'));
            setUrlZoneId(p.get('zone_id'));
        };
        window.addEventListener('popstate', onPop);
        return () => window.removeEventListener('popstate', onPop);
    }, []);

    useEffect(() => {
        if (!urlCampaignId) {
            setFieldSessionActive(false);
            setCampaignHomeOverlay(false);
            setCampaignHomeOverlayInSession(false);
        }
    }, [urlCampaignId]);

    const showExploreMapChrome = !urlCampaignId || campaignHomeOverlay;

    const openCampaignFromUrl = useCallback((campaignId: string, zoneId?: string | null) => {
        const url = new URL(window.location.href);
        url.searchParams.set('campaign_id', campaignId);
        if (zoneId) url.searchParams.set('zone_id', zoneId);
        else url.searchParams.delete('zone_id');
        window.history.pushState({}, '', url);
        setUrlCampaignId(campaignId);
        setUrlZoneId(zoneId ?? null);
        setCampaignPanelCollapsed(false);
    }, []);

    const selectCampaignZone = useCallback(
        (zoneId: string, opts?: { sessionRestore?: boolean }) => {
            if (!urlCampaignId || (fieldSessionActive && !opts?.sessionRestore)) return;
            const url = new URL(window.location.href);
            url.searchParams.set('campaign_id', urlCampaignId);
            url.searchParams.set('zone_id', zoneId);
            window.history.pushState({}, '', url);
            setUrlZoneId(zoneId);
            setCampaignWorkspaceResumeIdInSession(urlCampaignId, zoneId);
            setCampaignPanelCollapsed(false);
        },
        [urlCampaignId, fieldSessionActive],
    );

    const clearCampaignZone = useCallback(() => {
        if (!urlCampaignId || fieldSessionActive) return;
        const url = new URL(window.location.href);
        url.searchParams.set('campaign_id', urlCampaignId);
        url.searchParams.delete('zone_id');
        window.history.pushState({}, '', url);
        setUrlZoneId(null);
        setCampaignWorkspaceResumeIdInSession(urlCampaignId, null);
    }, [urlCampaignId, fieldSessionActive]);

    const clearCampaignMapOverlays = useCallback(() => {
        try {
            mapRef.current?.setCampaignMapData({ zones: null, live: null, fitBounds: false });
            mapRef.current?.clearHighlight();
        } catch {
            /* map style reload race */
        }
    }, []);

    /** Leave `?campaign_id=` / `zone_id` and return to the normal explore map. */
    const exitCampaignMode = useCallback(() => {
        clearCampaignMapOverlays();
        const url = new URL(window.location.href);
        url.searchParams.delete('campaign_id');
        url.searchParams.delete('zone_id');
        const qs = url.searchParams.toString();
        window.history.pushState({}, '', qs ? `${url.pathname}?${qs}` : url.pathname);
        setUrlCampaignId(null);
        setUrlZoneId(null);
        setCampaignPanelCollapsed(false);
        setSheetState('expanded');
        clearFieldCampaignsExploreEngagedFromSession();
        clearCampaignWorkspaceResumeIdFromSession();
        setFieldCampaignsChromeSuppressed(false);
        setCampaignHomeOverlay(false);
        setCampaignHomeOverlayInSession(false);
    }, [clearCampaignMapOverlays, setSheetState]);

    const goToCampaignHome = useCallback(() => {
        setCampaignHomeOverlay(true);
        setCampaignHomeOverlayInSession(true);
        setCampaignPanelCollapsed(true);
        setSheetState('expanded');
    }, [setSheetState]);

    const returnFromCampaignHome = useCallback(() => {
        setCampaignHomeOverlay(false);
        setCampaignHomeOverlayInSession(false);
        setCampaignPanelCollapsed(false);
        setSheetState('minimized');
    }, [setSheetState]);

    /** Profile / Settings → open the bottom “My field campaigns” picker. */
    const [myCampaignsExpandNonce, setMyCampaignsExpandNonce] = useState(0);
    /** Map control → open assignments list without engaging full field mode. */
    const [myCampaignsListOpenOnlyNonce, setMyCampaignsListOpenOnlyNonce] = useState(0);
    const [fieldAssignmentGate, setFieldAssignmentGate] = useState<
        | { status: 'unknown' }
        | { status: 'forbidden'; detail?: string }
        | { status: 'ready'; count: number }
    >({ status: 'unknown' });
    const requestOpenMyCampaignsPicker = useCallback(() => {
        setMyCampaignsExpandNonce((n) => n + 1);
    }, []);

    /** Field campaigns UI is engaged — keep home bottom sheet + map toggle hidden until Exit (confirmed). Persists across reload (session) while signed in. */
    const [fieldCampaignsChromeSuppressed, setFieldCampaignsChromeSuppressed] = useState(
        readFieldCampaignsExploreEngagedFromSession,
    );
    const [fieldLauncherCollapseKey, setFieldLauncherCollapseKey] = useState(0);
    const [confirmExitFieldCampaigns, setConfirmExitFieldCampaigns] = useState(false);

    const engageFieldCampaignsChrome = useCallback(() => {
        setFieldCampaignsChromeSuppressed(true);
        setFieldCampaignsExploreEngagedInSession(true);
    }, []);

    const confirmLeaveFieldCampaignsSurface = useCallback(() => {
        setFieldCampaignsChromeSuppressed(false);
        setFieldCampaignsExploreEngagedInSession(false);
        setFieldLauncherCollapseKey((k) => k + 1);
        setConfirmExitFieldCampaigns(false);
    }, []);

    const refreshFieldAssignmentCount = useCallback(async () => {
        if (!isAuthenticated || isHeroMode) {
            setFieldAssignmentGate({ status: 'unknown' });
            return;
        }
        try {
            const [assignments, zones] = await Promise.all([
                fetchMyCampaignAssignments(),
                fetchMyAssignedZones(),
            ]);
            const campaignIds = new Set<string>();
            for (const row of assignments) {
                if (row.id) campaignIds.add(row.id);
            }
            for (const zone of zones) {
                if (zone.campaign_id) campaignIds.add(zone.campaign_id);
            }
            setFieldAssignmentGate({ status: 'ready', count: campaignIds.size });
        } catch (e) {
            if (e instanceof APIError && e.status === 403) {
                setFieldAssignmentGate({
                    status: 'forbidden',
                    detail: e.message || undefined,
                });
            } else {
                setFieldAssignmentGate({ status: 'ready', count: 0 });
            }
        }
    }, [isAuthenticated, isHeroMode]);

    useEffect(() => {
        void refreshFieldAssignmentCount();
    }, [refreshFieldAssignmentCount, urlCampaignId]);

    const showFieldAssignmentsControl =
        fieldAssignmentGate.status === 'ready' && fieldAssignmentGate.count > 0;

    const fieldCampaignsMenuState = useMemo(() => {
        if (!isAuthenticated || isHeroMode) return null;
        if (fieldAssignmentGate.status === 'unknown') {
            return { disabled: true as const, subtitle: 'Checking access…' };
        }
        if (fieldAssignmentGate.status === 'forbidden') {
            return {
                disabled: true as const,
                subtitle:
                    fieldAssignmentGate.detail
                    || 'You need a campaign team assignment or assigned zone to use field campaigns on the map.',
            };
        }
        if (fieldAssignmentGate.count === 0) {
            return {
                disabled: true as const,
                subtitle: 'No campaigns assigned yet. Ask your campaign manager to add you to a team or assign a zone.',
            };
        }
        return { disabled: false as const, subtitle: 'Open your assigned campaign list on the map' };
    }, [isAuthenticated, isHeroMode, fieldAssignmentGate]);
    const exitCampaignAndOpenAssignmentsPicker = useCallback(() => {
        clearCampaignMapOverlays();
        const url = new URL(window.location.href);
        url.searchParams.delete('campaign_id');
        url.searchParams.delete('zone_id');
        const qs = url.searchParams.toString();
        window.history.pushState({}, '', qs ? `${url.pathname}?${qs}` : url.pathname);
        setUrlCampaignId(null);
        setUrlZoneId(null);
        setCampaignPanelCollapsed(false);
        setSheetState('expanded');
        setMyCampaignsExpandNonce((n) => n + 1);
    }, [clearCampaignMapOverlays, setSheetState]);

    useEffect(() => {
        if (urlCampaignId && !campaignHomeOverlay) {
            setSheetState('minimized');
        }
    }, [urlCampaignId, campaignHomeOverlay, setSheetState]);

    useEffect(() => {
        if (!urlCampaignId) return;
        setFieldCampaignsChromeSuppressed(false);
        clearFieldCampaignsExploreEngagedFromSession();
    }, [urlCampaignId]);

    useEffect(() => {
        if (urlCampaignId) {
            setCampaignWorkspaceResumeIdInSession(urlCampaignId, urlZoneId);
        } else {
            clearCampaignWorkspaceResumeIdFromSession();
        }
    }, [urlCampaignId, urlZoneId]);

    // --- Campaign auth gate: require login for ?campaign_id= URLs ---
    const campaignAuthPendingRef = useRef(false);
    const prevShowAuthModalRef = useRef(false);

    useEffect(() => {
        if (!urlCampaignId || isAuthenticated) {
            campaignAuthPendingRef.current = false;
            return;
        }
        // User has a campaign_id but is not authenticated — prompt login
        campaignAuthPendingRef.current = true;
        openAuthModal('login');
    }, [urlCampaignId, isAuthenticated, openAuthModal]);

    // If auth modal closes and user still isn't authenticated, exit campaign mode.
    // Guard: only react when the modal transitions open→closed (wasOpen), not on the
    // initial render where showAuthModal is false before openAuthModal has committed.
    useEffect(() => {
        const wasOpen = prevShowAuthModalRef.current;
        prevShowAuthModalRef.current = showAuthModal;

        if (!campaignAuthPendingRef.current) return;
        if (showAuthModal) return; // modal still open
        if (!wasOpen) return; // modal not yet opened — skip initial false state
        if (isAuthenticated) {
            // User just logged in — campaign will load normally
            campaignAuthPendingRef.current = false;
            return;
        }
        // Modal was dismissed without logging in — exit campaign
        campaignAuthPendingRef.current = false;
        exitCampaignMode();
    }, [showAuthModal, isAuthenticated, exitCampaignMode]);

    useLayoutEffect(() => {
        if (!urlCampaignId) return;
        const params = new URLSearchParams(window.location.search);
        const curCampaign = params.get('campaign_id');
        const curZone = params.get('zone_id');
        const campaignOk = curCampaign === urlCampaignId;
        const zoneOk = urlZoneId ? curZone === urlZoneId : !curZone;
        if (campaignOk && zoneOk) return;
        if (curCampaign && curCampaign !== urlCampaignId) return;
        const u = new URL(window.location.href);
        u.searchParams.set('campaign_id', urlCampaignId);
        if (urlZoneId) u.searchParams.set('zone_id', urlZoneId);
        else u.searchParams.delete('zone_id');
        window.history.replaceState({}, '', u.toString());
    }, [urlCampaignId, urlZoneId]);

    // Restore locked zone from active session as soon as the user is authenticated (e.g. after login).
    useEffect(() => {
        if (!isAuthenticated || !urlCampaignId || urlZoneId) return;
        let cancelled = false;
        void (async () => {
            try {
                const res = await fetchMyActiveCampaignSession();
                if (cancelled) return;
                if (!isSessionStatusActive(res.session?.status)) return;
                if (res.campaign_id && res.campaign_id !== urlCampaignId) return;
                const zid = res.focused_zone_id?.trim();
                if (!zid) return;
                selectCampaignZone(zid, { sessionRestore: true });
            } catch {
                /* panel will retry after workspace load */
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [isAuthenticated, urlCampaignId, urlZoneId, selectCampaignZone]);

    useEffect(() => {
        if (!fieldCampaignsChromeSuppressed) return;
        setControlsExpanded(false);
        useMapStore.getState().setShowSearchResults(false);
        useMapStore.getState().clearSearch();
        useMapStore.getState().clearSelectedPlace();
    }, [fieldCampaignsChromeSuppressed]);

    /** Hide explore-map chrome (search, expanded map controls) in field campaign mode. */
    useEffect(() => {
        if (!urlCampaignId) return;
        setControlsExpanded(false);
        useMapStore.getState().setShowSearchResults(false);
        useMapStore.getState().clearSearch();
        useMapStore.getState().clearSelectedPlace();
    }, [urlCampaignId]);

    // Keep app theme and map style in sync
    useEffect(() => {
        if (settings.theme === 'dark' && mapStyle === 'standard') {
            handleStyleChange('dark');
        } else if (settings.theme === 'light' && mapStyle === 'dark') {
            handleStyleChange('standard');
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [settings.theme]);

    // On mount: if saved map style is dark but theme is light, sync theme to dark
    useEffect(() => {
        if (mapStyle === 'dark' && settings.theme !== 'dark') {
            updateSetting('theme', 'dark');
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Handle OAuth callback on app load
    useEffect(() => {
        if (!hasCheckedOAuth.current) {
            hasCheckedOAuth.current = true;
            handleOAuthCallback();
        }
    }, [handleOAuthCallback]);

    // Handle email verification completion
    const handleVerificationComplete = () => {
        window.history.replaceState({}, '', '/');
        window.location.reload();
    };

    // Map settings state - synced with settings store, persisted to localStorage
    const [mapStyle, setMapStyle] = useState<MapStyle>(() => {
        const saved = localStorage.getItem('coordal-map-style');
        return (saved as MapStyle) || 'standard';
    });
    const isTerrainEnabled = settings.showTerrain;

    const handleToggleTerrain = (enabled: boolean) => {
        updateSetting('showTerrain', enabled);
        mapRef.current?.toggleTerrain(enabled);
    };


    // Unified layer settings state
    const [showLayerSettings, setShowLayerSettings] = useState(false);
    const [layerSettingsInitialSection, setLayerSettingsInitialSection] = useState<LayerSectionId | null>(null);
    const [layerSettings, setLayerSettings] = useState<LayerSettings>(DEFAULT_LAYER_SETTINGS);
    const [isWeatherView, setIsWeatherView] = useState(false);
    const [forecastVariable, setForecastVariable] = useState<ForecastVariable>('precip');
    const [forecastPeriod, setForecastPeriod] = useState<string | null>(null);
    const [forecastAvailablePeriods, setForecastAvailablePeriods] = useState<string[]>([]);
    const [showForecastSummary, setShowForecastSummary] = useState(true);
    const [weatherOutlookCollapsed, setWeatherOutlookCollapsed] = useState(true);
    const [showWeatherSubscribe, setShowWeatherSubscribe] = useState(false);
    const [showDailyBriefing, setShowDailyBriefing] = useState(false);
    const [mapCenter, setMapCenter] = useState<{ lng: number; lat: number }>({ lng: -1.5, lat: 7.9 });

    const briefingLat = userLocation?.lat ?? mapCenter.lat;
    const briefingLng = userLocation?.lng ?? mapCenter.lng;
    const canShowUserWeatherForecast = canShowWeatherForecast(userLocation);
    const weatherUnavailableMessage = canShowUserWeatherForecast
        ? null
        : weatherForecastUnavailableMessage(userLocation);
    const shouldFetchDailyBriefing =
        !IS_PLAYWRIGHT_TEST &&
        isMapLoaded && !isHeroMode && !urlCampaignId && !isWeatherView && canShowUserWeatherForecast;

    const liveForecastLocation = useLiveForecastLocation(
        isWeatherView,
        userLocation,
        mapCenter,
        setUserLocation,
    );
    const { isSubscribed: isWeatherSubscribed, markSubscribed: markWeatherSubscribed } =
        useWeatherSubscription(liveForecastLocation.lat, liveForecastLocation.lng);
    useRequestLocationForWeather(isWeatherView, userLocation, setUserLocation);

    const {
        days: forecastDays,
        hours: forecastHours,
        loading: forecastLoading,
        district_name: forecastDistrictName,
        region_name: forecastRegionName,
        country_name: forecastCountryName,
        postcode: forecastPostcode,
        district_code: forecastDistrictCode,
        service_area_code: forecastServiceAreaCode,
        cell_id: forecastCellId,
    } = useForecastAtPoint(
        liveForecastLocation.lat,
        liveForecastLocation.lng,
        isWeatherView && canShowUserWeatherForecast,
    );

    const {
        days: briefingDays,
        loading: briefingLoading,
        district_name: briefingDistrictName,
        region_name: briefingRegionName,
        country_name: briefingCountryName,
        postcode: briefingPostcode,
        district_code: briefingDistrictCode,
        service_area_code: briefingServiceAreaCode,
        cell_id: briefingCellId,
    } = useForecastAtPoint(briefingLat, briefingLng, shouldFetchDailyBriefing);

    const { isSubscribed: isBriefingSubscribed, markSubscribed: markBriefingSubscribed } =
        useWeatherSubscription(briefingLat, briefingLng);

    const forecastLocation = useMemo(
        () => ({
            cell_id: forecastCellId,
            district_code: forecastDistrictCode,
            service_area_code: forecastServiceAreaCode,
            district_name: forecastDistrictName,
            region_name: forecastRegionName,
            country_name: forecastCountryName,
            postcode: forecastPostcode,
        }),
        [
            forecastCellId,
            forecastDistrictCode,
            forecastServiceAreaCode,
            forecastDistrictName,
            forecastRegionName,
            forecastCountryName,
            forecastPostcode,
        ],
    );

    const forecastSummaryText = useMemo(
        () => buildForecastSummary(forecastDays),
        [forecastDays],
    );

    const briefingSummaryText = useMemo(
        () => buildForecastSummary(briefingDays),
        [briefingDays],
    );

    const briefingLocation = useMemo(
        () => ({
            cell_id: briefingCellId,
            district_code: briefingDistrictCode,
            service_area_code: briefingServiceAreaCode,
            district_name: briefingDistrictName,
            region_name: briefingRegionName,
            country_name: briefingCountryName,
            postcode: briefingPostcode,
        }),
        [
            briefingCellId,
            briefingDistrictCode,
            briefingServiceAreaCode,
            briefingDistrictName,
            briefingRegionName,
            briefingCountryName,
            briefingPostcode,
        ],
    );

    const dismissDailyBriefing = useCallback(() => {
        markWeatherDailyBriefingDismissed(briefingLat, briefingLng);
        setShowDailyBriefing(false);
    }, [briefingLat, briefingLng]);

    const handleDailyBriefingSubscribe = useCallback(() => {
        requireAuth(() => {
            setShowDailyBriefing(false);
            setShowWeatherSubscribe(true);
        });
    }, [requireAuth]);

    const forecastTileLayerForVariable = useCallback((variable: ForecastVariable) => {
        if (variable === 'temp') return 'weather_forecast_temp';
        if (variable === 'wind') return 'weather_forecast_wind';
        return 'weather_forecast_precip';
    }, []);

    const openWeatherView = useCallback(() => {
        const params = new URLSearchParams(window.location.search);
        params.set('view', 'weather');
        const query = params.toString();
        window.history.pushState({}, '', query ? `?${query}` : window.location.pathname);
        setIsWeatherView(true);
        setShowLayerSettings(false);
        setShowProfile(false);
        setShowSettings(false);
        setControlsExpanded(false);
        setSheetState('minimized');
        const forecastAvailable = canShowWeatherForecast(userLocation);
        setShowForecastSummary(forecastAvailable);
        setWeatherOutlookCollapsed(!forecastAvailable);
        setForecastPeriod((prev) => prev ?? new Date().toISOString().slice(0, 10));
    }, [setSheetState, userLocation]);

    const handleOpenFullForecastFromBriefing = useCallback(() => {
        markWeatherDailyBriefingDismissed(briefingLat, briefingLng);
        setShowDailyBriefing(false);
        openWeatherView();
    }, [briefingLat, briefingLng, openWeatherView]);

    const closeWeatherView = useCallback(() => {
        const params = new URLSearchParams(window.location.search);
        params.delete('view');
        const query = params.toString();
        window.history.replaceState({}, '', query ? `?${query}` : window.location.pathname);
        setIsWeatherView(false);
        setForecastPeriod(null);
        setLayerSettings((prev) => ({
            ...prev,
            forecastWeatherTemp: false,
            forecastWeatherPrecip: false,
            forecastWeatherWind: false,
            forecastWeatherTempPeriods: [],
            forecastWeatherPrecipPeriods: [],
            forecastWeatherWindPeriods: [],
        }));
    }, []);

    useEffect(() => {
        const params = new URLSearchParams(window.location.search);
        if (params.get('view') !== 'weather') return;
        params.delete('view');
        const query = params.toString();
        window.history.replaceState({}, '', query ? `?${query}` : window.location.pathname);
    }, []);

    useEffect(() => {
        const onPopState = () => {
            const open = new URLSearchParams(window.location.search).get('view') === 'weather';
            setIsWeatherView(open);
            if (!open) {
                setForecastPeriod(null);
                setLayerSettings((prev) => ({
                    ...prev,
                    forecastWeatherTemp: false,
                    forecastWeatherPrecip: false,
                    forecastWeatherWind: false,
                    forecastWeatherTempPeriods: [],
                    forecastWeatherPrecipPeriods: [],
                    forecastWeatherWindPeriods: [],
                }));
            }
        };
        window.addEventListener('popstate', onPopState);
        return () => window.removeEventListener('popstate', onPopState);
    }, []);

    useEffect(() => {
        if (!isWeatherView) return;
        setSheetState('minimized');
    }, [isWeatherView, setSheetState]);

    useEffect(() => {
        if (!isWeatherView || !canShowUserWeatherForecast) return;
        void fetchTiledPeriodManifest(forecastTileLayerForVariable(forecastVariable)).then(({ periods }) => {
            setForecastAvailablePeriods(periods);
            const pool = resolveOutlookPeriods(periods);
            if (pool.length > 0 && (!forecastPeriod || !pool.includes(forecastPeriod))) {
                setForecastPeriod(pool[0]);
            }
        });
    }, [isWeatherView, canShowUserWeatherForecast, forecastVariable, forecastTileLayerForVariable, forecastPeriod]);

    useEffect(() => {
        if (!isWeatherView) return;
        if (!canShowUserWeatherForecast) {
            setLayerSettings((prev) => ({
                ...prev,
                forecastWeatherTemp: false,
                forecastWeatherPrecip: false,
                forecastWeatherWind: false,
                forecastWeatherTempPeriods: [],
                forecastWeatherPrecipPeriods: [],
                forecastWeatherWindPeriods: [],
            }));
            return;
        }
        const periodList = forecastPeriod ? [forecastPeriod] : [];
        setLayerSettings((prev) => ({
            ...prev,
            forecastWeatherTemp: forecastVariable === 'temp',
            forecastWeatherPrecip: forecastVariable === 'precip',
            forecastWeatherWind: forecastVariable === 'wind',
            forecastWeatherTempPeriods: forecastVariable === 'temp' ? periodList : [],
            forecastWeatherPrecipPeriods: forecastVariable === 'precip' ? periodList : [],
            forecastWeatherWindPeriods: forecastVariable === 'wind' ? periodList : [],
        }));
    }, [isWeatherView, canShowUserWeatherForecast, forecastVariable, forecastPeriod]);

    useEffect(() => {
        if (canShowUserWeatherForecast) return;
        setLayerSettings((prev) => {
            const needsClear =
                prev.observedWeatherWindDirection
                || OBSERVED_WEATHER_UI_DEFS.some(
                    (def) => prev[def.settingsKey] || (prev[def.periodSettingsKey]?.length ?? 0) > 0,
                );
            if (!needsClear) return prev;
            const next = { ...prev };
            for (const def of OBSERVED_WEATHER_UI_DEFS) {
                next[def.settingsKey] = false;
                next[def.periodSettingsKey] = [];
            }
            next.observedWeatherWindDirection = false;
            next.observedWeatherWindDirectionPeriods = [];
            return next;
        });
    }, [canShowUserWeatherForecast]);

    useEffect(() => {
        setLayerSettings((prev) => (
            prev.liveTraffic === settings.trafficAlerts
                ? prev
                : { ...prev, liveTraffic: settings.trafficAlerts }
        ));
    }, [settings.trafficAlerts]);

    const handleToggleLayer = (layer: keyof LayerSettings, value: boolean) => {
        if (
            !canShowUserWeatherForecast
            && OBSERVED_WEATHER_LAYER_KEYS.includes(layer as typeof OBSERVED_WEATHER_LAYER_KEYS[number])
        ) {
            return;
        }
        setLayerSettings((prev) => {
            const next = { ...prev, [layer]: value };
            if (layer === 'observedWeatherWind') {
                next.observedWeatherWindDirection = value;
            }
            if (layer === 'buildings' || layer === 'buildings3D') {
                mapRef.current?.applyBuildingLayers(next.buildings, next.buildings3D);
            } else if (
                !OBSERVED_WEATHER_LAYER_KEYS.includes(layer as typeof OBSERVED_WEATHER_LAYER_KEYS[number])
                && !FORECAST_WEATHER_LAYER_KEYS.includes(layer as typeof FORECAST_WEATHER_LAYER_KEYS[number])
            ) {
                mapRef.current?.toggleLayer(layer, value);
            }
            return next;
        });
    };

    const handleToggleFloodYear = (year: number, enabled: boolean) => {
        setLayerSettings((prev) => {
            const years = new Set(prev.floodExposureYears);
            if (enabled) years.add(year);
            else years.delete(year);
            return { ...prev, floodExposureYears: [...years].sort((a, b) => a - b) };
        });
    };

    const handleObservedWeatherPeriodChange = (key: ObservedWeatherPeriodKey, period: string, enabled: boolean) => {
        if (!canShowUserWeatherForecast) return;
        setLayerSettings((prev) => {
            const next = new Set(prev[key]);
            if (enabled) next.add(period);
            else next.delete(period);
            const periods = [...next].sort();
            const updated = { ...prev, [key]: periods };
            if (key === 'observedWeatherWindPeriods') {
                updated.observedWeatherWindDirectionPeriods = periods;
            }
            return updated;
        });
    };

    const climateActiveLayer = useMemo((): ObservedWeatherLayerKey | null => {
        const active = OBSERVED_WEATHER_UI_DEFS.find((def) => layerSettings[def.settingsKey]);
        return active?.settingsKey ?? null;
    }, [layerSettings]);

    const climateSelectedMonths = useMemo((): string[] => {
        if (!climateActiveLayer) return [];
        const def = OBSERVED_WEATHER_UI_DEFS.find((d) => d.settingsKey === climateActiveLayer);
        if (!def) return [];
        return layerSettings[def.periodSettingsKey] ?? [];
    }, [climateActiveLayer, layerSettings]);

    const focusMapForClimateLayer = useCallback(() => {
        const zoom = OBSERVED_WEATHER_TILE_MIN_ZOOM;
        mapRef.current?.flyTo(liveForecastLocation, zoom, { bottomPadding: 300 });
    }, [liveForecastLocation]);

    const handleWeatherClimateLayerSelect = useCallback(
        (layer: ObservedWeatherLayerKey | null) => {
            if (!canShowWeatherForecast(userLocation)) return;
            setLayerSettings((prev) => {
                const next = { ...prev };
                for (const def of OBSERVED_WEATHER_UI_DEFS) {
                    const on = def.settingsKey === layer;
                    next[def.settingsKey] = on;
                    if (!on) {
                        next[def.periodSettingsKey] = [];
                    }
                }
                next.observedWeatherWindDirection = layer === 'observedWeatherWind';
                if (layer !== 'observedWeatherWind') {
                    next.observedWeatherWindDirectionPeriods = [];
                }
                return next;
            });
            if (layer) {
                focusMapForClimateLayer();
            }
        },
        [focusMapForClimateLayer, userLocation],
    );

    const handleWeatherClimateMonthToggle = useCallback((period: string, enabled: boolean) => {
        if (!canShowWeatherForecast(userLocation)) return;
        setLayerSettings((prev) => {
            const activeDef = OBSERVED_WEATHER_UI_DEFS.find((d) => prev[d.settingsKey]);
            if (!activeDef) return prev;
            const next = new Set(prev[activeDef.periodSettingsKey]);
            if (enabled) next.add(period);
            else next.delete(period);
            const periods = [...next].sort();
            const updated = {
                ...prev,
                [activeDef.periodSettingsKey]: periods,
            };
            if (activeDef.settingsKey === 'observedWeatherWind') {
                updated.observedWeatherWindDirectionPeriods = periods;
            }
            return updated;
        });
    }, [userLocation]);

    const handleForecastWeatherPeriodChange = (key: ForecastWeatherPeriodKey, period: string, enabled: boolean) => {
        setLayerSettings((prev) => {
            const next = new Set(prev[key]);
            if (enabled) next.add(period);
            else next.delete(period);
            return { ...prev, [key]: [...next].sort() };
        });
        if (isWeatherView && enabled) {
            setForecastPeriod(period);
        }
    };

    // Navigation state
    const [showDirections, setShowDirections] = useState(false);
    const [isNavigating, setIsNavigating] = useState(false);
    const [stopNavDestination, setStopNavDestination] = useState<{ name: string; coords: { lat: number; lng: number } } | null>(null);

    // Surface seasonal flood exposure on the map while reviewing routes.
    useEffect(() => {
        if (!showDirections || routes.length === 0) return;
        setLayerSettings((prev) => {
            if (prev.floodExposure) return prev;
            const next = { ...prev, floodExposure: true };
            mapRef.current?.toggleLayer('floodExposure', true);
            return next;
        });
    }, [showDirections, routes.length]);

    // Deep link: ?flood_exposure=1 turns on the seasonal exposure overlay.
    useEffect(() => {
        const params = new URLSearchParams(window.location.search);
        if (params.get('flood_exposure') !== '1' && params.get('flood') !== '1') return;
        setLayerSettings((prev) => {
            if (prev.floodExposure) return prev;
            const next = { ...prev, floodExposure: true };
            mapRef.current?.toggleLayer('floodExposure', true);
            return next;
        });
    }, []);

    // Shared controls expanded state (Settings2 toggles left FAB + right map controls)
    const [controlsExpanded, setControlsExpanded] = useState(false);

    // Panel states
    const [showProfile, setShowProfile] = useState(false);
    const [showSettings, setShowSettings] = useState(false);
    const [settingsInitialSection, setSettingsInitialSection] = useState<SettingsPanelSection | undefined>(undefined);

    // Deep link: ?settings=appearance|navigation|privacy|language|help|feedback
    useEffect(() => {
        if (isHeroMode) return;
        const params = new URLSearchParams(window.location.search);
        const s = params.get('settings');
        const allowed: SettingsPanelSection[] = [
            'appearance',
            'navigation',
            'privacy',
            'security',
            'language',
            'help',
            'feedback',
        ];
        if (!s || !allowed.includes(s as SettingsPanelSection)) return;
        setSettingsInitialSection(s as SettingsPanelSection);
        setShowSettings(true);
        params.delete('settings');
        const qs = params.toString();
        window.history.replaceState({}, '', qs ? `${window.location.pathname}?${qs}` : window.location.pathname);
    }, [isHeroMode]);
    const [showAddPlace, setShowAddPlace] = useState(false);
    const [addPlaceLabel, setAddPlaceLabel] = useState<'home' | 'work' | 'saved'>('saved');
    const [addPlacePrefill, setAddPlacePrefill] = useState<{ name?: string; address?: string; coords?: { lat: number; lng: number }; postcode?: string; placeId?: string } | null>(null);

    // Add Location state
    const [showAddLocation, setShowAddLocation] = useState(false);
    const [showMyContributions, setShowMyContributions] = useState(false);
    const [showSavedPlaces, setShowSavedPlaces] = useState(false);
    const [showRecentActivity, setShowRecentActivity] = useState(false);
    const [contributionLocation, setContributionLocation] = useState<{ lat: number; lng: number } | null>(null);
    const [drawnGeometry, setDrawnGeometry] = useState<GeoJSON.Geometry | null>(null);
    const [editingContribution, setEditingContribution] = useState<ContributionDetail | null>(null);
    const [suggestEditPlace, setSuggestEditPlace] = useState<Place | null>(null);
    const [isMapClickEnabled, setIsMapClickEnabled] = useState(false);
    const [showFindPostcode, setShowFindPostcode] = useState(false);
    const [findPostcodeLocation, setFindPostcodeLocation] = useState<{ lat: number; lng: number } | null>(null);
    const [isFindPostcodeMapClick, setIsFindPostcodeMapClick] = useState(false);
    const [panelReturnTo, setPanelReturnTo] = useState<'profile' | 'settings' | 'savedPlaces' | null>(null);
    const [findPostcodeSnapshot, setFindPostcodeSnapshot] = useState<FindPostcodeSnapshot | null>(null);
    const [isDrawingEnabled, setIsDrawingEnabled] = useState(false);
    const [markingDrawFabActive, setMarkingDrawFabActive] = useState(false);
    const [contributionDrawLineMode, setContributionDrawLineMode] = useState(false);
    const [roadSnappingEnabled, setRoadSnappingEnabled] = useState(true);
    const [snapStatusMessage, setSnapStatusMessage] = useState<string | null>(null);
    const [roadSnapConnections, setRoadSnapConnections] = useState<SnapConnectionRecord[]>([]);
    const traceSnapAfterStopRef = useRef(false);
    /** Skip clearing snap metadata when draw.update comes from post-snap geometry replace. */
    const drawGeometrySyncFromSnapRef = useRef(false);
    /** Skip clearing snap overlays when tracePoints update comes from endpoint snap. */
    const traceSnapSyncRef = useRef(false);
    const [traceDraftMeta, setTraceDraftMeta] = useState<Pick<FieldTraceDraft, 'featureTypeId' | 'landmarkGroup'> | null>(() => {
        const draft = readFieldTraceDraft();
        if (!draft) return null;
        return {
            featureTypeId: draft.featureTypeId,
            landmarkGroup: draft.landmarkGroup,
        };
    });
    const [isTracing, setIsTracing] = useState(false);
    const [tracePoints, setTracePoints] = useState<Array<{ lat: number; lng: number }>>(() => {
        return readFieldTraceDraft()?.points ?? [];
    });
    const [traceGpsAccuracyM, setTraceGpsAccuracyM] = useState<number | null>(null);
    const [traceGpsSkippedCount, setTraceGpsSkippedCount] = useState(0);
    const [traceSamplingMode, setTraceSamplingMode] = useState<TraceSamplingMode>(() => {
        return readFieldTraceDraft()?.samplingMode ?? 'distance';
    });
    const [traceMinDistanceM, setTraceMinDistanceM] =
        useState<TraceDistanceOptionM>(() => {
            return readFieldTraceDraft()?.minDistanceM ?? DEFAULT_TRACE_MIN_DISTANCE_M;
        });
    const [contributionTraceRingMode, setContributionTraceRingMode] = useState(() => {
        return readFieldTraceDraft()?.ringMode ?? false;
    });
    const [traceResampleOnStop, setTraceResampleOnStop] = useState(() => {
        return readFieldTraceDraft()?.resampleOnStop ?? false;
    });
    const [traceCaptureMode, setTraceCaptureMode] = useState<TraceCaptureMode>(() => {
        return readFieldTraceDraft()?.captureMode ?? 'continuous';
    });
    const [isAddingTraceCorner, setIsAddingTraceCorner] = useState(false);
    /** Manual thin/finer controls — enabled after stop-trace cleanup (or restored draft). */
    const [traceRefineReady, setTraceRefineReady] = useState(() => {
        return (readFieldTraceDraft()?.points.length ?? 0) >= 2;
    });
    /** Path after automatic stop cleanup — reset target for manual thin/finer. */
    const [tracePathBaseline, setTracePathBaseline] = useState<Array<{ lat: number; lng: number }> | null>(null);
    /** Raw GPS capture at stop, before resting/outlier cleanup. */
    const [tracePointsOriginal, setTracePointsOriginal] = useState<Array<{ lat: number; lng: number }> | null>(null);
    const [tracePathModified, setTracePathModified] = useState(false);
    const [tracePostProcessSummary, setTracePostProcessSummary] = useState<string | null>(null);
    const [selectedTraceVertexIndex, setSelectedTraceVertexIndex] = useState<number | null>(null);
    const [traceVertexEditEnabled, setTraceVertexEditEnabled] = useState(false);
    type TracePoint = { lat: number; lng: number };
    const traceEditPastRef = useRef<TracePoint[][]>([]);
    const traceEditFutureRef = useRef<TracePoint[][]>([]);
    const [traceEditCanUndo, setTraceEditCanUndo] = useState(false);
    const [traceEditCanRedo, setTraceEditCanRedo] = useState(false);

    const syncTraceEditHistoryUi = useCallback(() => {
        setTraceEditCanUndo(traceEditPastRef.current.length > 0);
        setTraceEditCanRedo(traceEditFutureRef.current.length > 0);
    }, []);

    const clearTraceEditHistory = useCallback(() => {
        traceEditPastRef.current = [];
        traceEditFutureRef.current = [];
        syncTraceEditHistoryUi();
    }, [syncTraceEditHistoryUi]);

    const cloneTracePoints = (pts: TracePoint[]) => pts.map((p) => ({ ...p }));

    const recordTraceEditSnapshot = useCallback((pts: TracePoint[]) => {
        traceEditPastRef.current.push(cloneTracePoints(pts));
        if (traceEditPastRef.current.length > 40) {
            traceEditPastRef.current.shift();
        }
        traceEditFutureRef.current = [];
        syncTraceEditHistoryUi();
    }, [syncTraceEditHistoryUi]);

    const tracePathRefineEditing =
        traceVertexEditEnabled && traceCaptureMode !== 'corners';

    const flyToContributionGeometry = useCallback((detail: ContributionDetail) => {
        const boundsGeometry = contributionMapBoundsGeometry(detail);
        const geometryType = parseStoredGeometry(detail.geometry)?.type;
        if (
            boundsGeometry?.type === 'GeometryCollection'
            || geometryType === 'Polygon'
            || geometryType === 'MultiPolygon'
            || geometryType === 'MultiLineString'
        ) {
            if (boundsGeometry) {
                mapRef.current?.fitCampaignGeometry(boundsGeometry as GeoJSON.Geometry, {
                    maxZoom: 17,
                    padding: 80,
                });
            }
            return;
        }

        const coords = detail.geometry?.coordinates;
        if (detail.geometry?.type === 'Point' && coords) {
            mapRef.current?.flyTo({ lng: coords[0] as number, lat: coords[1] as number }, 17);
        } else if (detail.geometry?.type === 'LineString' && Array.isArray(coords?.[0])) {
            const mid = (coords as number[][])[Math.floor((coords as number[][]).length / 2)];
            if (mid) mapRef.current?.flyTo({ lng: mid[0], lat: mid[1] }, 17);
        }
    }, []);

    const renderContributionGeometryOnMap = useCallback((detail: ContributionDetail) => {
        if (detail.status === 'approved' && detail.place_id) {
            mapRef.current?.previewContributionGuideGeometries(null);
            mapRef.current?.clearHighlight();
            return;
        }

        const guides = getDividedCarriagewayGuides(detail);
        const geometries = collectContributionGeometries(detail);

        if (guides) {
            mapRef.current?.clearHighlight();
            mapRef.current?.previewContributionGuideGeometries(guides);
            return;
        }
        mapRef.current?.previewContributionGuideGeometries(null);
        const primary = geometries[0] ?? null;
        if (primary) {
            mapRef.current?.highlightGeometry(primary);
        } else {
            mapRef.current?.clearHighlight();
        }
    }, []);

    const closeContributionPreview = useCallback(() => {
        setViewingContribution(null);
        mapRef.current?.previewContributionGuideGeometries(null);
        mapRef.current?.clearHighlight();
        clearSelectedPlace();
        setSheetState('collapsed');
        stripContributionDeepLinkParams();
        clearContributionDeepLinkId();
    }, [clearSelectedPlace, setSheetState]);

    const showContributionOnMap = useCallback((detail: ContributionDetail) => {
        try {
            setViewingContribution(detail);
            writeContributionDeepLinkToUrl(
                detail.id,
                readContributionDeepLinkCoords() ?? contributionDeepLinkCoords(detail),
            );
            if (detail.status === 'approved' && detail.place_id) {
                mapRef.current?.previewContributionGuideGeometries(null);
                mapRef.current?.clearHighlight();
                void selectPlace(detail.place_id);
                setSheetState('half');
                flyToContributionGeometry(detail);
                return;
            }
            const previewPlace = contributionDetailToPreviewPlace(detail);
            setSelectedPlaceFromData(previewPlace, { fly: false });
            setSheetState('half');
            renderContributionGeometryOnMap(detail);
            flyToContributionGeometry(detail);
        } catch {
            if (import.meta.env.DEV) {
                console.warn('[coordal] Could not build contribution map preview');
            }
        }
    }, [flyToContributionGeometry, renderContributionGeometryOnMap, setSelectedPlaceFromData, setSheetState, selectPlace]);

    useEffect(() => {
        if (!selectedPlace || isContributionPreviewPlaceId(selectedPlace.id)) return;
        if (
            viewingContribution?.status === 'approved'
            && viewingContribution.place_id === selectedPlace.id
        ) {
            return;
        }
        setViewingContribution(null);
    }, [selectedPlace?.id, viewingContribution?.id, viewingContribution?.place_id, viewingContribution?.status]);

    // Keep contribution preview geometry on the map across style reloads and place-selection races.
    useEffect(() => {
        if (!isMapLoaded || !viewingContribution) return;

        renderContributionGeometryOnMap(viewingContribution);

        const map = mapRef.current?.getMap();
        if (!map) return;

        const reapply = () => {
            renderContributionGeometryOnMap(viewingContribution);
        };
        const onStyleLoad = () => reapply();
        const onIdle = () => reapply();
        map.on('style.load', onStyleLoad);
        map.once('idle', onIdle);
        return () => {
            map.off('style.load', onStyleLoad);
        };
    }, [isMapLoaded, viewingContribution, renderContributionGeometryOnMap]);

    const [showLocationPrompt, setShowLocationPrompt] = useState(false);

    // Map bearing/pitch/zoom state for compass and indicators
    const [mapBearing, setMapBearing] = useState(0);
    const [mapPitch, setMapPitch] = useState(0);
    const [mapZoom, setMapZoom] = useState(12);

    // Map bearing/pitch/zoom for UI — do not push center into mapStore on every tick (causes flyTo loops).
    useEffect(() => {
        if (!isMapLoaded) return;

        const map = mapRef.current?.getMap();
        if (!map) return;

        const updateUi = () => {
            if (!mapRef.current) return;
            setMapBearing(mapRef.current.getBearing());
            setMapPitch(mapRef.current.getPitch());
            setMapZoom(mapRef.current.getZoom());
            const c = mapRef.current.getCenter();
            setMapCenter(c);
        };

        const syncStoreOnMoveEnd = () => {
            const c = map.getCenter();
            useMapStore.getState().setCenter({ lat: c.lat, lng: c.lng });
            useMapStore.getState().setZoom(map.getZoom());
        };

        updateUi();
        map.on('move', updateUi);
        map.on('moveend', syncStoreOnMoveEnd);

        return () => {
            map.off('move', updateUi);
            map.off('moveend', syncStoreOnMoveEnd);
        };
    }, [isMapLoaded]);

    // Product tour: campaign mode gets field-workflow steps; explore map gets the default tour (never both).
    useEffect(() => {
        if (!isMapLoaded || isHeroMode) return;
        const timer = setTimeout(() => {
            if (urlCampaignId) {
                if (!hasCompletedCampaign) {
                    setSheetState('minimized');
                    startCampaignTour();
                }
            } else if (!hasCompleted) {
                setSheetState('minimized');
                setControlsExpanded(false);
                startTour();
            }
        }, 1500);
        return () => clearTimeout(timer);
    }, [
        isMapLoaded,
        isHeroMode,
        urlCampaignId,
        hasCompleted,
        hasCompletedCampaign,
        startTour,
        startCampaignTour,
        setSheetState,
    ]);

    // Daily weather briefing — once per day on the explore map, after the product tour finishes.
    useEffect(() => {
        if (!shouldFetchDailyBriefing || !canShowUserWeatherForecast) {
            setShowDailyBriefing(false);
            return;
        }
        if (tourIsActive) return;
        if (!hasCompleted) return;
        if (isBriefingSubscribed) return;
        if (isWeatherDailyBriefingDismissed(briefingLat, briefingLng)) return;
        if (briefingLoading && briefingDays.length === 0) return;

        const timer = window.setTimeout(() => setShowDailyBriefing(true), 600);
        return () => window.clearTimeout(timer);
    }, [
        shouldFetchDailyBriefing,
        canShowUserWeatherForecast,
        tourIsActive,
        hasCompleted,
        isBriefingSubscribed,
        briefingLat,
        briefingLng,
        briefingLoading,
        briefingDays.length,
    ]);

    // Auto-locate user on map load (disabled in hero mode to avoid permission prompts)
    // Uses "double opt-in" pattern: only triggers the native OS permission dialog
    // if user already granted permission. Otherwise shows our custom prompt first.
    useEffect(() => {
        if (!isMapLoaded || hasAutoLocated.current || isHeroMode) return;
        if (!navigator.geolocation) return;

        const locateUser = () => {
            requestCurrentPosition()
                .then((position) => {
                    const location = mapPositionToCoordinates(position);
                    setUserLocation(location);
                    flyToLocation(location, MAP_DEFAULT_ZOOM);
                    hasAutoLocated.current = true;
                    setShowLocationPrompt(false);
                })
                .catch(() => {
                    hasAutoLocated.current = true;
                    setShowLocationPrompt(true);
                });
        };

        // Check permission state before prompting
        if (navigator.permissions && navigator.permissions.query) {
            navigator.permissions.query({ name: 'geolocation' }).then((result) => {
                if (result.state === 'granted') {
                    locateUser();
                } else {
                    // 'prompt' or 'denied' — show our custom card/pill
                    // The LocationPermissionPrompt handles the UX internally
                    hasAutoLocated.current = true;
                    setShowLocationPrompt(true);
                }
            }).catch(() => {
                // Permissions API not supported — fall back to direct request
                locateUser();
            });
        } else {
            // Permissions API not available (older browsers) — fall back
            locateUser();
        }
    }, [isMapLoaded, setUserLocation, flyToLocation, isHeroMode]);

    // Refresh GPS when the user returns to the tab (fixes stale dot after moving).
    useEffect(() => {
        if (!isMapLoaded || isHeroMode) return;

        const refreshLocation = () => {
            if (document.visibilityState !== 'visible' || !navigator.geolocation) return;
            navigator.permissions?.query({ name: 'geolocation' })
                .then((result) => {
                    if (result.state !== 'granted') return;
                    requestCurrentPosition()
                        .then((position) => setUserLocation(mapPositionToCoordinates(position)))
                        .catch(() => undefined);
                })
                .catch(() => undefined);
        };

        document.addEventListener('visibilitychange', refreshLocation);
        return () => document.removeEventListener('visibilitychange', refreshLocation);
    }, [isMapLoaded, isHeroMode, setUserLocation]);

    useEffect(() => {
        if (userLocation) {
            setShowLocationPrompt(false);
        }
    }, [userLocation]);

    // Handle shared place link: ?lat=...&lng=...&zoom=...&place=...
    const hasHandledShareLink = useRef(false);
    useEffect(() => {
        if (!isMapLoaded || hasHandledShareLink.current) return;
        const params = new URLSearchParams(window.location.search);
        // Skip if this is a contribution deep-link (handled separately)
        if (params.get('contribution')) return;
        const lat = parseFloat(params.get('lat') || '');
        const lng = parseFloat(params.get('lng') || '');
        const zoom = parseInt(params.get('zoom') || '', 10);
        const placeId = params.get('place');

        if (!isNaN(lat) && !isNaN(lng)) {
            hasHandledShareLink.current = true;
            // Skip auto-locate when opening a share link
            hasAutoLocated.current = true;
            flyToLocation({ lat, lng }, !isNaN(zoom) ? zoom : MAP_FOCUS_ZOOM);

            if (placeId) {
                selectPlace(decodeURIComponent(placeId));
            }
        }
    }, [isMapLoaded, flyToLocation, selectPlace]);

    // Handle contribution deep-link: ?contribution=<id>&lat=...&lng=...&zoom=...
    const pendingContribId = useRef<string | null>(null);
    const inFlightContribDeepLinks = useRef(new Set<string>());
    const loadedContribDeepLinks = useRef(new Set<string>());

    const loadContribution = useCallback(async (contribId: string) => {
        if (loadedContribDeepLinks.current.has(contribId)) {
            return;
        }
        try {
            const detail = await getContribution(contribId);
            loadedContribDeepLinks.current.add(contribId);
            showContributionOnMap(detail);
            clearContributionDeepLinkId();
        } catch (err) {
            loadedContribDeepLinks.current.add(contribId);
            if (err instanceof APIError && err.status === 404) {
                clearContributionDeepLinkId();
            }
            if (import.meta.env.DEV) {
                console.warn('[coordal] Could not load contribution detail');
            }
        } finally {
            inFlightContribDeepLinks.current.delete(contribId);
        }
    }, [showContributionOnMap]);

    const openContributionFromNotification = useCallback(
        (contribId: string) => {
            requireAuth(() => {
                void loadContribution(contribId);
            });
        },
        [requireAuth, loadContribution],
    );

    const {
        toast: contributionNotification,
        dismissToast: dismissContributionNotification,
        openToast: openContributionNotification,
    } = useContributionNotifications(isAuthenticated, openContributionFromNotification);

    useEffect(() => {
        if (!isMapLoaded) return;

        const params = new URLSearchParams(window.location.search);
        const urlContribId = params.get('contribution');
        if (urlContribId) {
            stashContributionDeepLinkId(urlContribId);
            hasAutoLocated.current = true;

            const coords = readContributionDeepLinkCoords();
            if (coords) {
                flyToLocation({ lat: coords.lat, lng: coords.lng }, coords.zoom);
            }
        }

        const contribId = readContributionDeepLinkId();
        if (
            !contribId
            || inFlightContribDeepLinks.current.has(contribId)
            || loadedContribDeepLinks.current.has(contribId)
        ) return;

        pendingContribId.current = contribId;
        requireAuth(() => {
            if (inFlightContribDeepLinks.current.has(contribId)) return;
            inFlightContribDeepLinks.current.add(contribId);
            void loadContribution(contribId);
            pendingContribId.current = null;
        });
    }, [isMapLoaded, flyToLocation, requireAuth, loadContribution]);

    // After login completes, load the pending contribution if auth modal was shown
    useEffect(() => {
        if (!isAuthenticated) return;
        const contribId = pendingContribId.current ?? readContributionDeepLinkId();
        if (
            !contribId
            || inFlightContribDeepLinks.current.has(contribId)
            || loadedContribDeepLinks.current.has(contribId)
        ) return;

        pendingContribId.current = null;
        inFlightContribDeepLinks.current.add(contribId);
        void loadContribution(contribId);
    }, [isAuthenticated, loadContribution]);

    const handleStyleChange = (style: MapStyle) => {
        setMapStyle(style);
        localStorage.setItem('coordal-map-style', style);
        // Auto-sync app theme when map style changes
        if (style === 'dark' && settings.theme !== 'dark') {
            updateSetting('theme', 'dark');
        } else if (style !== 'dark' && settings.theme === 'dark') {
            updateSetting('theme', 'light');
        }
    };

    const handleGetDirections = () => {
        // Require auth for directions
        requireAuth(() => {
            if (selectedPlace) {
                useMapStore.getState().warnOnLocationFeatureUse();
                setShowDirections(true);
            }
        });
    };

    const handleNavigateToStop = (name: string, coords: { lat: number; lng: number }) => {
        useMapStore.getState().warnOnLocationFeatureUse();
        useDirectionsStore.getState().clearDirections();
        useDirectionsStore.getState().setTravelMode('walking');
        setStopNavDestination({ name, coords });
        setShowDirections(true);
    };

    const handleStartNavigation = () => {
        const { userLocation } = useMapStore.getState();
        if (!canStartLiveNavigation(userLocation)) {
            return;
        }
        setShowDirections(false);
        setIsNavigating(true);
    };

    const handleCloseNavigation = () => {
        setIsNavigating(false);
        triggerSurvey('navigation');
    };

    const handleSavePlace = () => {
        if (selectedPlace) {
            // Build full address with postcode
            const addressParts = [selectedPlace.address];
            if (selectedPlace.ghanaPostCode && !addressIncludesPostcode(selectedPlace.address, selectedPlace.ghanaPostCode)) {
                addressParts.push(selectedPlace.ghanaPostCode);
            }
            setAddPlaceLabel('saved');
            setAddPlacePrefill({
                name: selectedPlace.name,
                address: addressParts.join(', '),
                coords: selectedPlace.coordinates,
                postcode: selectedPlace.ghanaPostCode || '',
                placeId: selectedPlace.id,
            });
            setShowAddPlace(true);
        }
    };

    // Add Location handlers
    const handleOpenAddLocation = useCallback((opts?: { campaignFieldCapture?: boolean }) => {
        setControlsExpanded(false);
        setCampaignFieldCapture(opts?.campaignFieldCapture ?? false);
        if (urlCampaignId) {
            setCampaignHomeOverlay(true);
            setCampaignHomeOverlayInSession(true);
            setCampaignPanelCollapsed(true);
            setSheetState('minimized');
        }
        setShowAddLocation(true);
    }, [urlCampaignId, setSheetState]);

    const contributionPlaywrightRef = useRef({
        openAddLocation: () => handleOpenAddLocation(),
        setContributionLocation: (_loc: { lat: number; lng: number }) => {},
        setDrawnGeometry: (_geometry: GeoJSON.Geometry | null) => {},
        getDrawnGeometry: (): GeoJSON.Geometry | null => null,
        isAddLocationOpen: (): boolean => false,
        triggerContributionDrawComplete: (_geometry: GeoJSON.Geometry) => Promise.resolve(),
        getRoadSnapConnections: (): SnapConnectionRecord[] => [],
        getSnapStatusMessage: (): string | null => null,
    });
    const handleContributionDrawCompleteRef = useRef<
        (geometry: GeoJSON.Geometry) => Promise<void>
    >(async () => {});
    useEffect(() => {
        if ((!import.meta.env.DEV && !IS_PLAYWRIGHT_TEST) || typeof window === 'undefined') return;
        const w = window as unknown as {
            __coordalContributionTest: typeof contributionPlaywrightRef.current;
        };
        w.__coordalContributionTest = {
            openAddLocation: () => contributionPlaywrightRef.current.openAddLocation(),
            setContributionLocation: (loc) => contributionPlaywrightRef.current.setContributionLocation(loc),
            setDrawnGeometry: (geometry) => contributionPlaywrightRef.current.setDrawnGeometry(geometry),
            getDrawnGeometry: () => contributionPlaywrightRef.current.getDrawnGeometry(),
            isAddLocationOpen: () => contributionPlaywrightRef.current.isAddLocationOpen(),
            triggerContributionDrawComplete: (geometry) =>
                contributionPlaywrightRef.current.triggerContributionDrawComplete(geometry),
            getRoadSnapConnections: () => contributionPlaywrightRef.current.getRoadSnapConnections(),
            getSnapStatusMessage: () => contributionPlaywrightRef.current.getSnapStatusMessage(),
        };
        const onLoc = (event: Event) => {
            const detail = (event as CustomEvent<{ lat: number; lng: number }>).detail;
            if (detail) contributionPlaywrightRef.current.setContributionLocation(detail);
        };
        const onDraw = (event: Event) => {
            const detail = (event as CustomEvent<GeoJSON.Geometry | null>).detail;
            contributionPlaywrightRef.current.setDrawnGeometry(detail ?? null);
        };
        window.addEventListener('coordal-playwright-contribution-location', onLoc);
        window.addEventListener('coordal-playwright-drawn-geometry', onDraw);
        return () => {
            window.removeEventListener('coordal-playwright-contribution-location', onLoc);
            window.removeEventListener('coordal-playwright-drawn-geometry', onDraw);
        };
    }, []);

    const handleOpenContributionFromCampaign = useCallback(
        (opts?: { captureLiveLocation?: boolean }) => {
            if (opts?.captureLiveLocation) {
                const loc = userLocation;
                if (!loc) {
                    openLocationPrompt();
                    return;
                }
                setContributionLocation({ lat: loc.lat, lng: loc.lng });
                mapRef.current?.placeContributionMarker(loc.lat, loc.lng);
                mapRef.current?.flyTo({ lng: loc.lng, lat: loc.lat }, 17);
                handleOpenAddLocation({ campaignFieldCapture: true });
                return;
            }
            handleOpenAddLocation({ campaignFieldCapture: false });
        },
        [userLocation, handleOpenAddLocation],
    );

    const handleCloseAddLocation = () => {
        setShowAddLocation(false);
        setIsMapClickEnabled(false);
        setIsDrawingEnabled(false);
        setMarkingDrawFabActive(false);
        setIsTracing(false);
        clearTraceWatch();
        setContributionLocation(null);
        setDrawnGeometry(null);
        setEditingContribution(null);
        setSuggestEditPlace(null);
        setCampaignFieldCapture(false);
        setContributionTraceRingMode(false);
        mapRef.current?.clearHighlight();
        mapRef.current?.clearContributionPreview();
        mapRef.current?.unlockZoom();
    };

    useEffect(() => {
        if (!fieldCaptureLive && showAddLocation && campaignFieldCapture) {
            handleCloseAddLocation();
        }
    }, [fieldCaptureLive, showAddLocation, campaignFieldCapture]);

    const handleClearMapHighlight = useCallback(() => {
        mapRef.current?.clearHighlight();
    }, []);

    const handleViewContributionOnMap = (detail: ContributionDetail) => {
        setShowMyContributions(false);
        showContributionOnMap(detail);
    };

    const handleEditContribution = (detail: ContributionDetail) => {
        setShowMyContributions(false);
        setEditingContribution(detail);
        setIsTracing(false);
        clearTraceWatch();
        setTracePoints([]);
        setTracePathBaseline(null);
        setTracePointsOriginal(null);
        setTraceRefineReady(false);
        setTracePathModified(false);
        setTracePostProcessSummary(null);
        setDrawnGeometry(null);
        setRoadSnapConnections([]);
        setSnapStatusMessage(null);
        setSelectedTraceVertexIndex(null);
        setTraceVertexEditEnabled(false);
        clearTraceEditHistory();
        mapRef.current?.previewContributionGeometry(null);
        mapRef.current?.setSnapConnectionOverlays([]);

        const guides = getDividedCarriagewayGuides(detail);
        const geom = detail.geometry;
        if (geom?.type === 'Point') {
            const [lng, lat] = geom.coordinates as [number, number];
            setContributionLocation({ lat, lng });
            mapRef.current?.placeContributionMarker(lat, lng);
            mapRef.current?.flyTo({ lng, lat }, 17);
        } else if (guides) {
            setTracePoints(
                guides[0].geometry.coordinates.map(([lng, lat]) => ({ lat, lng })),
            );
            setDrawnGeometry(guides[0].geometry as GeoJSON.Geometry);
            renderContributionGeometryOnMap(detail);
            flyToContributionGeometry(detail);
        } else if (geom?.type === 'LineString' && detail.feature_type === 'road') {
            const coords = geom.coordinates as number[][];
            const points = coords.map(([lng, lat]) => ({ lat, lng }));
            setTracePoints(points);
            setTracePathBaseline(points.map((point) => ({ ...point })));
            setTracePointsOriginal(points.map((point) => ({ ...point })));
            setTraceRefineReady(true);
            setTracePathModified(false);
            setDrawnGeometry(null);
            setRoadSnapConnections([]);
            setSelectedTraceVertexIndex(null);
            mapRef.current?.clearHighlight();
            const mid = coords[Math.floor(coords.length / 2)];
            if (mid) mapRef.current?.flyTo({ lng: mid[0], lat: mid[1] }, 17);
        } else if (geom?.type === 'LineString') {
            setDrawnGeometry(geom as GeoJSON.Geometry);
            renderContributionGeometryOnMap(detail);
            flyToContributionGeometry(detail);
        } else if (geom) {
            setDrawnGeometry(geom as GeoJSON.Geometry);
            renderContributionGeometryOnMap(detail);
            flyToContributionGeometry(detail);
        }
        setShowAddLocation(true);
    };

    const handleEnableMapClick = (enabled: boolean) => {
        setIsMapClickEnabled(enabled);
    };

    const handleEnableFindPostcodeMapClick = (enabled: boolean) => {
        setIsFindPostcodeMapClick(enabled);
    };

    const closeFindPostcode = (clearReturnTo = true) => {
        setShowFindPostcode(false);
        setFindPostcodeLocation(null);
        setIsFindPostcodeMapClick(false);
        if (clearReturnTo) setPanelReturnTo(null);
        setFindPostcodeSnapshot(null);
        mapRef.current?.removeContributionMarker();
        setSheetState('collapsed');
    };

    const navigateToPanelReturn = (target: 'profile' | 'settings' | 'savedPlaces' | null) => {
        if (target === 'profile') {
            setShowProfile(true);
        } else if (target === 'settings') {
            setShowSettings(true);
        } else if (target === 'savedPlaces') {
            setShowSavedPlaces(true);
        }
    };

    const handlePanelNavigateBack = (closePanel: () => void) => {
        const returnTo = panelReturnTo;
        setPanelReturnTo(null);
        closePanel();
        navigateToPanelReturn(returnTo);
    };

    const closeMapOverlayPanels = () => {
        setShowProfile(false);
        setShowSettings(false);
        setSettingsInitialSection(undefined);
        setShowSavedPlaces(false);
        setShowRecentActivity(false);
        setShowMyContributions(false);
        setShowAddPlace(false);
        setShowFindPostcode(false);
        setFindPostcodeLocation(null);
        setIsFindPostcodeMapClick(false);
        setPanelReturnTo(null);
    };

    const openFindPostcode = (returnTo?: 'profile' | 'settings' | 'savedPlaces') => {
        closeMapOverlayPanels();
        setPanelReturnTo(returnTo ?? null);
        setFindPostcodeSnapshot(null);
        setShowFindPostcode(true);
        setFindPostcodeLocation(null);
        setSheetState('minimized');
    };

    const handleFindPostcodeNavigateBack = () => {
        handlePanelNavigateBack(() => closeFindPostcode(false));
    };

    const dismissSuggestEditToFindPostcode = () => {
        setShowAddLocation(false);
        setSuggestEditPlace(null);
        setIsMapClickEnabled(false);
        setIsDrawingEnabled(false);
        setContributionLocation(null);
        setDrawnGeometry(null);
        mapRef.current?.clearHighlight();
        mapRef.current?.removeContributionMarker();
        if (findPostcodeSnapshot) {
            setFindPostcodeLocation(findPostcodeSnapshot.location);
            setShowFindPostcode(true);
            setSheetState('minimized');
        }
    };

    const openProfile = () => {
        closeMapOverlayPanels();
        setShowProfile(true);
    };

    const openSettings = (section?: SettingsPanelSection) => {
        closeMapOverlayPanels();
        setPanelReturnTo(null);
        setSettingsInitialSection(section);
        setShowSettings(true);
    };

    const handleEnableDrawing = (enabled: boolean, options?: { lineMode?: boolean }) => {
        setIsDrawingEnabled(enabled);
        if (enabled) {
            setContributionDrawLineMode(options?.lineMode ?? false);
        }
    };

    const traceWatchIdRef = useRef<number | null>(null);
    const traceLogLastAtRef = useRef(0);
    const traceUserLocLastAtRef = useRef(0);
    const traceSamplingModeRef = useRef<TraceSamplingMode>(traceSamplingMode);
    const traceMinDistanceMRef = useRef<TraceDistanceOptionM>(traceMinDistanceM);
    const TRACE_USER_LOC_MIN_MS = 750;

    useEffect(() => {
        traceSamplingModeRef.current = traceSamplingMode;
    }, [traceSamplingMode]);

    useEffect(() => {
        traceMinDistanceMRef.current = traceMinDistanceM;
    }, [traceMinDistanceM]);

    const clearTraceWatch = () => {
        if (traceWatchIdRef.current !== null) {
            navigator.geolocation.clearWatch(traceWatchIdRef.current);
            traceWatchIdRef.current = null;
        }
    };

    const invalidateTraceSnapState = useCallback(() => {
        setRoadSnapConnections([]);
        setSnapStatusMessage(null);
    }, []);

    const handleUndoTracePoint = () => {
        setTracePoints((prev) => prev.slice(0, -1));
        setDrawnGeometry(null);
        setTracePostProcessSummary(null);
        invalidateTraceSnapState();
        setSelectedTraceVertexIndex(null);
    };

    const handleUndoFirstTracePoint = () => {
        setTracePoints((prev) => (prev.length <= 1 ? [] : prev.slice(1)));
        setDrawnGeometry(null);
        setTracePostProcessSummary(null);
        invalidateTraceSnapState();
        setSelectedTraceVertexIndex(null);
    };

    const handleDeleteSelectedTraceVertex = useCallback(() => {
        if (selectedTraceVertexIndex == null) return;
        setTracePoints((prev) => {
            if (selectedTraceVertexIndex < 0 || selectedTraceVertexIndex >= prev.length) {
                return prev;
            }
            const next = prev.filter((_, i) => i !== selectedTraceVertexIndex);
            if (next.length === prev.length) return prev;
            if (tracePathRefineEditing) {
                recordTraceEditSnapshot(prev);
            }
            setTracePathModified(true);
            setTracePostProcessSummary(
                next.length >= 2
                    ? `Point removed: ${prev.length} → ${next.length} points`
                    : null,
            );
            return next;
        });
        setDrawnGeometry(null);
        invalidateTraceSnapState();
        setSelectedTraceVertexIndex(null);
    }, [
        selectedTraceVertexIndex,
        invalidateTraceSnapState,
        tracePathRefineEditing,
        recordTraceEditSnapshot,
    ]);

    const handleTraceEditUndo = useCallback(() => {
        const past = traceEditPastRef.current;
        if (past.length === 0) return;
        traceEditFutureRef.current.unshift(cloneTracePoints(tracePoints));
        const previous = past.pop()!;
        setTracePoints(previous);
        setDrawnGeometry(null);
        invalidateTraceSnapState();
        setSelectedTraceVertexIndex(null);
        setTracePathModified(true);
        setTracePostProcessSummary('Undid last change');
        syncTraceEditHistoryUi();
    }, [tracePoints, invalidateTraceSnapState, syncTraceEditHistoryUi]);

    const handleTraceEditRedo = useCallback(() => {
        const future = traceEditFutureRef.current;
        if (future.length === 0) return;
        traceEditPastRef.current.push(cloneTracePoints(tracePoints));
        const next = future.shift()!;
        setTracePoints(next);
        setDrawnGeometry(null);
        invalidateTraceSnapState();
        setSelectedTraceVertexIndex(null);
        setTracePathModified(true);
        setTracePostProcessSummary('Redid change');
        syncTraceEditHistoryUi();
    }, [tracePoints, invalidateTraceSnapState, syncTraceEditHistoryUi]);

    const handleHydrateMarkingGeometry = useCallback(
        (payload: {
            geometryMode: 'draw' | 'trace' | 'point';
            drawnGeometry: GeoJSON.Geometry | null;
            tracePoints: Array<{ lat: number; lng: number }>;
            markingFeatureType: string | null;
        }) => {
            mapRef.current?.clearHighlight();
            const { geometryMode, drawnGeometry, tracePoints } = payload;

            if (geometryMode === 'trace' && tracePoints.length > 0) {
                mapRef.current?.previewContributionGeometry(null);
                return;
            }

            if (
                geometryMode === 'draw'
                && drawnGeometry
                && (drawnGeometry.type === 'LineString' || drawnGeometry.type === 'Polygon')
            ) {
                window.setTimeout(() => {
                    drawGeometrySyncFromSnapRef.current = true;
                    mapRef.current?.drawReplaceGeometry(
                        drawnGeometry as GeoJSON.LineString | GeoJSON.Polygon,
                    );
                    mapRef.current?.drawEnterVertexEdit();
                    queueMicrotask(() => {
                        drawGeometrySyncFromSnapRef.current = false;
                    });
                }, 50);
                return;
            }

            if (drawnGeometry) {
                mapRef.current?.previewContributionGeometry(drawnGeometry);
            }
        },
        [],
    );

    const handleAddTraceCorner = useCallback(async () => {
        setIsAddingTraceCorner(true);
        try {
            const position = await waitForFieldMappingPosition();
            const accuracyM =
                typeof position.coords.accuracy === 'number'
                && Number.isFinite(position.coords.accuracy)
                    ? position.coords.accuracy
                    : null;
            if (!isGpsAccuracyAcceptableForFieldMapping(accuracyM)) {
                throw new Error(
                    `GPS accuracy too low (${accuracyM != null ? `${Math.round(accuracyM)} m` : 'unknown'}). Move to an open area and try again.`,
                );
            }
            const newPoint = {
                lat: position.coords.latitude,
                lng: position.coords.longitude,
            };
            setUserLocation(mapPositionToCoordinates(position));
            if (accuracyM != null) {
                setTraceGpsAccuracyM(accuracyM);
            }
            setTracePoints((prev) => [...prev, newPoint]);
            setTracePostProcessSummary(null);
            setTracePathModified(false);
            mapRef.current?.flyTo({ lng: newPoint.lng, lat: newPoint.lat }, 20);
            if (import.meta.env.DEV) {
                console.info('[coordal:trace] corner added', {
                    index: tracePoints.length + 1,
                    lat: newPoint.lat,
                    lng: newPoint.lng,
                    accuracyM,
                });
            }
        } finally {
            setIsAddingTraceCorner(false);
        }
    }, [setUserLocation, tracePoints.length]);

    const handleStartTrace = () => {
        clearTraceEditHistory();
        setIsTracing(true);
        setTraceRefineReady(false);
        setTracePathModified(false);
        setTracePostProcessSummary(null);
        if (tracePoints.length === 0) {
            traceLogLastAtRef.current = 0;
            traceUserLocLastAtRef.current = 0;
            setTraceGpsAccuracyM(null);
            setTraceGpsSkippedCount(0);
        }
        if (import.meta.env.DEV) {
            console.info('[coordal:trace] watch started');
        }

        // Start watching GPS position
        if ('geolocation' in navigator) {
            traceWatchIdRef.current = navigator.geolocation.watchPosition(
                (position) => {
                    const accuracyM =
                        typeof position.coords.accuracy === 'number'
                        && Number.isFinite(position.coords.accuracy)
                            ? position.coords.accuracy
                            : null;
                    if (accuracyM != null) {
                        setTraceGpsAccuracyM(accuracyM);
                    }

                    const newPoint = {
                        lat: position.coords.latitude,
                        lng: position.coords.longitude,
                    };
                    const now = Date.now();
                    const shouldUpdateUserLoc =
                        traceUserLocLastAtRef.current === 0
                        || now - traceUserLocLastAtRef.current >= TRACE_USER_LOC_MIN_MS;
                    if (shouldUpdateUserLoc) {
                        traceUserLocLastAtRef.current = now;
                        setUserLocation(mapPositionToCoordinates(position));
                    }

                    if (!isGpsAccuracyAcceptableForFieldMapping(accuracyM)) {
                        setTraceGpsSkippedCount((count) => count + 1);
                        if (import.meta.env.DEV) {
                            console.info('[coordal:trace] skipped poor GPS fix', {
                                accuracyM,
                                lat: newPoint.lat,
                                lng: newPoint.lng,
                            });
                        }
                        return;
                    }

                    setTracePoints((prev) => {
                        const lastAccepted = prev.length > 0 ? prev[prev.length - 1] : null;
                        if (
                            !shouldAcceptTracePoint(
                                newPoint,
                                lastAccepted,
                                traceSamplingModeRef.current,
                                traceMinDistanceMRef.current,
                            )
                        ) {
                            return prev;
                        }
                        const next = [...prev, newPoint];
                        if (import.meta.env.DEV) {
                            const shouldLog =
                                next.length === 1 ||
                                next.length % 10 === 0 ||
                                now - traceLogLastAtRef.current > 5000;
                            if (shouldLog) {
                                traceLogLastAtRef.current = now;
                                console.info('[coordal:trace]', {
                                    pointCount: next.length,
                                    lat: newPoint.lat,
                                    lng: newPoint.lng,
                                    accuracyM: position.coords.accuracy ?? null,
                                });
                            }
                        }
                        return next;
                    });
                },
                (error) => {
                    console.error('GPS error:', error);
                    openLocationPrompt();
                    setIsTracing(false);
                },
                {
                    enableHighAccuracy: true,
                    maximumAge: 1000,
                    timeout: 10000,
                }
            );
        } else {
            openLocationPrompt();
            setIsTracing(false);
        }
    };

    const handleStopTrace = () => {
        setIsTracing(false);
        clearTraceWatch();
        setTracePoints((prev) => {
            setTracePointsOriginal(prev.map((point) => ({ ...point })));
            const result = postProcessTracePath(prev, {
                resample: traceResampleOnStop,
                resampleSpacingM: traceMinDistanceM,
            });
            setTracePathBaseline(result.points.map((point) => ({ ...point })));
            setTracePathModified(false);
            if (result.rawCount > 0 && result.finalCount !== result.rawCount) {
                const steps = result.resampled
                    ? 'resting/outliers + resample'
                    : 'resting/outliers';
                setTracePostProcessSummary(
                    `Path refined (${steps}): ${result.rawCount} → ${result.finalCount} points`,
                );
            } else {
                setTracePostProcessSummary(null);
            }
            if (import.meta.env.DEV) {
                console.info('[coordal:trace] post-process', {
                    raw: result.rawCount,
                    afterResting: result.afterRestingCount,
                    afterOutliers: result.afterOutlierCount,
                    resampled: result.resampled,
                    final: result.finalCount,
                });
            }
            return result.points;
        });
        setTraceRefineReady(true);
        clearTraceEditHistory();
        traceSnapAfterStopRef.current = true;
        if (import.meta.env.DEV) {
            console.info('[coordal:trace] watch stopped');
        }
    };

    const handleThinTrace = () => {
        invalidateTraceSnapState();
        setSelectedTraceVertexIndex(null);
        setDrawnGeometry(null);
        setTracePoints((prev) => {
            if (prev.length < 2) return prev;
            const spacingM = pickThinSpacingM(traceMinDistanceM);
            const next = thinTracePath(prev, spacingM);
            if (next.length === prev.length) return prev;
            if (tracePathRefineEditing) {
                recordTraceEditSnapshot(prev);
            }
            setTracePathModified(true);
            setTracePostProcessSummary(
                `Path thinned (~${spacingM} m): ${prev.length} → ${next.length} points`,
            );
            return next;
        });
    };

    const handleResampleFinerTrace = () => {
        invalidateTraceSnapState();
        setSelectedTraceVertexIndex(null);
        setDrawnGeometry(null);
        setTracePoints((prev) => {
            if (prev.length < 2) return prev;
            const spacingM = pickFinerSpacingM(traceMinDistanceM);
            if (spacingM == null) return prev;
            const next = resampleFinerTracePath(prev, spacingM);
            if (next.length === prev.length) return prev;
            if (tracePathRefineEditing) {
                recordTraceEditSnapshot(prev);
            }
            setTracePathModified(true);
            setTracePostProcessSummary(
                `Path densified (~${spacingM} m): ${prev.length} → ${next.length} points`,
            );
            return next;
        });
    };

    const canResetTracePath = Boolean(
        tracePointsOriginal?.length || tracePathBaseline?.length,
    );

    const handleResetTracePath = () => {
        const source = tracePointsOriginal?.length
            ? tracePointsOriginal
            : tracePathBaseline;
        if (!source?.length) return;
        setTracePoints(source.map((point) => ({ ...point })));
        setTracePathModified(false);
        setTracePostProcessSummary(null);
        setDrawnGeometry(null);
        invalidateTraceSnapState();
        setSelectedTraceVertexIndex(null);
    };

    const handleRestoreRawTrace = () => {
        if (!tracePointsOriginal?.length) return;
        setTracePoints(tracePointsOriginal.map((point) => ({ ...point })));
        setTracePathModified(true);
        setTracePostProcessSummary(null);
    };

    const handleRemoveTraceOutliers = () => {
        invalidateTraceSnapState();
        setSelectedTraceVertexIndex(null);
        setDrawnGeometry(null);
        setTracePoints((prev) => {
            if (prev.length < 3) return prev;
            const next = removeTraceOutliers(prev);
            if (next.length === prev.length) return prev;
            setTracePathModified(true);
            setTracePostProcessSummary(
                `Spikes removed: ${prev.length} → ${next.length} points`,
            );
            return next;
        });
    };

    const handleStraightenTrace = () => {
        invalidateTraceSnapState();
        setSelectedTraceVertexIndex(null);
        setDrawnGeometry(null);
        setTracePoints((prev) => {
            if (prev.length < 3) return prev;
            const next = straightenTracePath(prev);
            if (next.length === prev.length) return prev;
            setTracePathModified(true);
            setTracePostProcessSummary(
                `Path straightened: ${prev.length} → ${next.length} points`,
            );
            return next;
        });
    };

    type SnapEndpointsContext = {
        featureTypeId: string | null;
        geometryMode: 'draw' | 'trace';
    };

    const handleSnapEndpointsNow = useCallback(async (context: SnapEndpointsContext) => {
        const isPolygon = context.featureTypeId
            ? traceProducesPolygon(context.featureTypeId)
            : !contributionDrawLineMode;

        setRoadSnapConnections([]);
        setSnapStatusMessage(null);

        if (context.geometryMode === 'draw' && drawnGeometry) {
            if (!isPolygon && drawnGeometry.type === 'LineString') {
                try {
                    const snapped = await snapLineStringEndpoints(drawnGeometry as GeoJSON.LineString, {
                        bands: roadSnapBands,
                        autoOnly: false,
                        resampleSpacingM: traceMinDistanceM,
                        ...roadSnapFetchOptions,
                    });
                    setDrawnGeometry(snapped.geometry);
                    if (snapped.connections.length) {
                        setRoadSnapConnections(snapped.connections);
                    }
                    setSnapStatusMessage(snapped.message ?? 'No snap within search distance');
                    drawGeometrySyncFromSnapRef.current = true;
                    mapRef.current?.drawReplaceGeometry(snapped.geometry);
                    queueMicrotask(() => {
                        drawGeometrySyncFromSnapRef.current = false;
                    });
                } catch {
                    setSnapStatusMessage('Snap failed — try again');
                }
                return;
            }
            if (isPolygon && drawnGeometry.type === 'Polygon') {
                const ring = (drawnGeometry as GeoJSON.Polygon).coordinates[0] as [number, number][];
                const open =
                    ring.length > 1
                    && ring[0][0] === ring[ring.length - 1][0]
                    && ring[0][1] === ring[ring.length - 1][1]
                        ? (ring.slice(0, -1) as [number, number][])
                        : (ring as [number, number][]);
                const closed = tryClosePolygonRing(open);
                if (closed.closed) {
                    const next = { type: 'Polygon' as const, coordinates: [closed.ring] };
                    setDrawnGeometry(next);
                    setSnapStatusMessage(
                        closed.gapM != null
                            ? `Boundary closed (${closed.gapM.toFixed(1)} m gap)`
                            : 'Boundary closed',
                    );
                    drawGeometrySyncFromSnapRef.current = true;
                    mapRef.current?.drawReplaceGeometry(next);
                    queueMicrotask(() => {
                        drawGeometrySyncFromSnapRef.current = false;
                    });
                } else {
                    setSnapStatusMessage(
                        closed.gapM != null
                            ? `Start and end are ${closed.gapM.toFixed(1)} m apart — move a vertex closer`
                            : 'Need at least 3 points to close',
                    );
                }
            }
            return;
        }

        if (context.geometryMode === 'trace' && tracePoints.length >= 2) {
            if (isPolygon) {
                const open = tracePoints.map((p) => [p.lng, p.lat] as [number, number]);
                const closed = tryClosePolygonRing(open);
                if (closed.closed) {
                    const open = closed.ring.slice(0, -1).map(([lng, lat]) => ({ lat, lng }));
                    const resampled = resampleTracePathIfGap(open, traceMinDistanceM);
                    setTracePoints(resampled);
                    setTracePathModified(true);
                    setDrawnGeometry(null);
                    setSnapStatusMessage(
                        closed.gapM != null
                            ? `Perimeter closed (${closed.gapM.toFixed(1)} m gap)`
                            : 'Perimeter closed',
                    );
                } else {
                    setSnapStatusMessage(
                        closed.gapM != null
                            ? `First and last corner are ${closed.gapM.toFixed(1)} m apart`
                            : 'Need at least 3 corners to close',
                    );
                }
                return;
            }
            const line: GeoJSON.LineString = {
                type: 'LineString',
                coordinates: tracePoints.map((p) => [p.lng, p.lat]),
            };
            try {
                const snapped = await snapLineStringEndpoints(line, {
                    bands: roadSnapBands,
                    autoOnly: false,
                    resampleSpacingM: traceMinDistanceM,
                    ...roadSnapFetchOptions,
                });
                traceSnapSyncRef.current = true;
                setRoadSnapConnections(snapped.connections);
                setSnapStatusMessage(snapped.message ?? 'No snap within search distance');
                setTracePoints(
                    snapped.geometry.coordinates.map((c) => ({ lng: c[0], lat: c[1] })),
                );
                setTracePathModified(true);
                setDrawnGeometry(null);
                setSelectedTraceVertexIndex(null);
                queueMicrotask(() => {
                    traceSnapSyncRef.current = false;
                });
            } catch {
                setSnapStatusMessage('Snap failed — try again');
            }
        }
    }, [contributionDrawLineMode, drawnGeometry, roadSnapBands, roadSnapFetchOptions, tracePoints, traceMinDistanceM]);

    const canRestoreRawTrace = Boolean(
        tracePointsOriginal
        && tracePathBaseline
        && (
            tracePointsOriginal.length !== tracePathBaseline.length
            || tracePointsOriginal.some((point, index) => {
                const baseline = tracePathBaseline[index];
                return !baseline || point.lat !== baseline.lat || point.lng !== baseline.lng;
            })
        ),
    );

    const showFieldMappingGpsPill = isTracing || isAddingTraceCorner || fieldCaptureLive || (showAddLocation && isMapClickEnabled);
    const fieldMappingGpsAccuracyM = isTracing
        ? traceGpsAccuracyM ?? userLocation?.accuracyM ?? null
        : userLocation?.accuracyM ?? null;
    const fieldMappingGpsSkippedCount = isTracing ? traceGpsSkippedCount : 0;

    const handleContributionUseMyLocation = useCallback(async () => {
        const position = await requestCurrentPosition();
        const location = mapPositionToCoordinates(position);
        setUserLocation(location);
        setContributionLocation({ lat: location.lat, lng: location.lng });
        mapRef.current?.placeContributionMarker(location.lat, location.lng);
        mapRef.current?.flyTo({ lng: location.lng, lat: location.lat }, 20);
    }, [setUserLocation]);

    const handleClearGeometry = (options?: { keepTrace?: boolean }) => {
        setContributionLocation(null);
        setDrawnGeometry(null);
        if (!options?.keepTrace) {
            setTracePoints([]);
            setTraceGpsAccuracyM(null);
            setTraceGpsSkippedCount(0);
            setTraceDraftMeta(null);
            setTracePostProcessSummary(null);
            setTraceRefineReady(false);
            setTracePathBaseline(null);
            setTracePointsOriginal(null);
            setTracePathModified(false);
            clearFieldTraceDraft();
        }
        setSnapStatusMessage(null);
        setRoadSnapConnections([]);
        setSelectedTraceVertexIndex(null);
        setTraceVertexEditEnabled(false);
        clearTraceEditHistory();
        traceSnapAfterStopRef.current = false;
        mapRef.current?.removeContributionMarker();
        mapRef.current?.previewContributionGeometry(null);
        mapRef.current?.setSnapConnectionOverlays([]);
    };

    const handleContributionDrawGeometryChange = useCallback((geometry: GeoJSON.Geometry) => {
        setDrawnGeometry(geometry);
        if (!drawGeometrySyncFromSnapRef.current) {
            setRoadSnapConnections([]);
            setSnapStatusMessage(null);
        }
    }, []);

    const handleContributionDrawComplete = useCallback(async (geometry: GeoJSON.Geometry) => {
        let next = geometry;
        if (roadSnappingEnabled) {
            if (contributionDrawLineMode && geometry.type === 'LineString') {
                try {
                    const snapped = await snapLineStringEndpoints(geometry as GeoJSON.LineString, {
                        bands: roadSnapBands,
                        resampleSpacingM: traceMinDistanceM,
                        ...roadSnapFetchOptions,
                    });
                    next = snapped.geometry;
                    if (snapped.connections.length) {
                        setRoadSnapConnections(snapped.connections);
                    }
                    setSnapStatusMessage(snapped.message);
                    drawGeometrySyncFromSnapRef.current = true;
                    mapRef.current?.drawReplaceGeometry(snapped.geometry);
                    queueMicrotask(() => {
                        drawGeometrySyncFromSnapRef.current = false;
                    });
                } catch {
                    setSnapStatusMessage(null);
                }
            } else if (!contributionDrawLineMode && geometry.type === 'Polygon') {
                const ring = (geometry as GeoJSON.Polygon).coordinates[0] as [number, number][];
                const open =
                    ring.length > 1
                    && ring[0][0] === ring[ring.length - 1][0]
                    && ring[0][1] === ring[ring.length - 1][1]
                        ? (ring.slice(0, -1) as [number, number][])
                        : (ring as [number, number][]);
                const closed = tryClosePolygonRing(open);
                if (closed.closed) {
                    next = { type: 'Polygon', coordinates: [closed.ring] };
                    setSnapStatusMessage(
                        closed.gapM != null
                            ? `Boundary closed (${closed.gapM.toFixed(1)} m gap)`
                            : 'Boundary closed',
                    );
                    drawGeometrySyncFromSnapRef.current = true;
                    mapRef.current?.drawReplaceGeometry(next as GeoJSON.Polygon);
                    queueMicrotask(() => {
                        drawGeometrySyncFromSnapRef.current = false;
                    });
                }
            }
        }
        setDrawnGeometry(next);
    }, [contributionDrawLineMode, roadSnappingEnabled, roadSnapBands, roadSnapFetchOptions, traceMinDistanceM]);

    handleContributionDrawCompleteRef.current = handleContributionDrawComplete;
    contributionPlaywrightRef.current = {
        openAddLocation: () => handleOpenAddLocation(),
        setContributionLocation: (loc) => setContributionLocation(loc),
        setDrawnGeometry: (geometry) => setDrawnGeometry(geometry),
        getDrawnGeometry: () => drawnGeometry,
        isAddLocationOpen: () => showAddLocation,
        triggerContributionDrawComplete: (geometry) => handleContributionDrawCompleteRef.current(geometry),
        getRoadSnapConnections: () => roadSnapConnections,
        getSnapStatusMessage: () => snapStatusMessage,
    };

    useEffect(() => {
        if (!traceSnapAfterStopRef.current || !traceRefineReady || tracePoints.length < 2) {
            return;
        }
        const featureTypeId = traceDraftMeta?.featureTypeId ?? null;
        const isPolygon = featureTypeId ? traceProducesPolygon(featureTypeId) : false;
        if (!roadSnappingEnabled || isPolygon) {
            traceSnapAfterStopRef.current = false;
            return;
        }
        traceSnapAfterStopRef.current = false;
        const line: GeoJSON.LineString = {
            type: 'LineString',
            coordinates: tracePoints.map((p) => [p.lng, p.lat]),
        };
        void snapLineStringEndpoints(line, {
            bands: roadSnapBands,
            resampleSpacingM: traceMinDistanceM,
            ...roadSnapFetchOptions,
        }).then((snapped) => {
            if (snapped.connections.length === 0) {
                if (snapped.message) setSnapStatusMessage(snapped.message);
                return;
            }
            traceSnapSyncRef.current = true;
            setRoadSnapConnections(snapped.connections);
            setSnapStatusMessage(snapped.message);
            setTracePoints(
                snapped.geometry.coordinates.map((c) => ({ lng: c[0], lat: c[1] })),
            );
            setTracePathModified(true);
            setDrawnGeometry(null);
            queueMicrotask(() => {
                traceSnapSyncRef.current = false;
            });
        }).catch(() => {
            setSnapStatusMessage(null);
        });
    }, [traceRefineReady, tracePoints, roadSnappingEnabled, traceDraftMeta?.featureTypeId, roadSnapBands, roadSnapFetchOptions, traceMinDistanceM]);

    useEffect(() => {
        if (traceSnapSyncRef.current || isTracing) return;
        if (tracePoints.length === 0) {
            setRoadSnapConnections([]);
            return;
        }
        setRoadSnapConnections((prev) => (prev.length > 0 ? [] : prev));
        setSnapStatusMessage((msg) => (msg ? null : msg));
    }, [tracePoints, isTracing]);

    const showPendingSurveyOverlay =
        isAuthenticated && (showAddLocation || fieldSessionActive || fieldCaptureLive);

    useEffect(() => {
        if (showAddLocation) {
            setPendingSurveyRefreshNonce((n) => n + 1);
        }
    }, [showAddLocation]);

    usePendingSurveyGeometries({
        enabled: showPendingSurveyOverlay,
        mapRef,
        isMapLoaded,
        mapCenter,
        mapZoom,
        campaignId: urlCampaignId,
        refreshNonce: pendingSurveyRefreshNonce,
    });

    const handlePreviewContributionGeometry = useCallback((geo: GeoJSONGeometry | null) => {
        mapRef.current?.previewContributionGeometry(geo as GeoJSON.Geometry | null);
    }, []);

    useEffect(() => {
        mapRef.current?.setSnapConnectionOverlays(roadSnapConnections);
    }, [roadSnapConnections]);

    const handlePreviewContributionGuideGeometries = useCallback((
        guides: Array<{ side: 'a' | 'b' | 'divide'; geometry: GeoJSON.LineString }> | null,
    ) => {
        mapRef.current?.previewContributionGuideGeometries(guides);
    }, []);

    const handleTraceContextChange = useCallback((
        context: Pick<FieldTraceDraft, 'featureTypeId' | 'landmarkGroup'>,
    ) => {
        setTraceDraftMeta(context);
    }, []);

    useEffect(() => {
        if (tracePoints.length === 0) {
            clearFieldTraceDraft();
            return;
        }
        const timeoutId = window.setTimeout(() => {
            writeFieldTraceDraft({
                points: tracePoints,
                featureTypeId: traceDraftMeta?.featureTypeId ?? null,
                landmarkGroup: traceDraftMeta?.landmarkGroup ?? null,
                samplingMode: traceSamplingMode,
                minDistanceM: traceMinDistanceM,
                ringMode: contributionTraceRingMode,
                resampleOnStop: traceResampleOnStop,
                captureMode: traceCaptureMode,
                savedAt: Date.now(),
            });
        }, 300);
        return () => window.clearTimeout(timeoutId);
    }, [
        tracePoints,
        traceDraftMeta,
        traceSamplingMode,
        traceMinDistanceM,
        contributionTraceRingMode,
        traceResampleOnStop,
        traceCaptureMode,
    ]);

    // Show email verification page if on that route
    if (verificationToken) {
        return <EmailVerification token={verificationToken} onComplete={handleVerificationComplete} />;
    }

    // Show password reset page if on that route
    if (resetToken) {
        return <ResetPassword token={resetToken} onComplete={() => {
            window.history.replaceState({}, '', '/');
            window.location.reload();
        }} />;
    }

    // Show navigation view if navigating - use directions store data
    if (isNavigating && origin && destination && routes.length > 0) {
        return (
            <NavigationView
                destination={destination.name}
                destinationCoords={destination.coordinates}
                originCoords={origin.coordinates}
                isDarkMode={mapStyle === 'dark' || settings.theme === 'dark'}
                onClose={handleCloseNavigation}
            />
        );
    }

    return (
        <div className="relative w-full h-full overflow-hidden bg-[var(--bg-secondary)]">
            {/* Map */}
            <MapContainer
                ref={mapRef}
                mapStyle={mapStyle}
                isTerrainEnabled={isTerrainEnabled}
                isContributionMode={isMapClickEnabled || isFindPostcodeMapClick}
                isDrawingMode={isDrawingEnabled && !traceVertexEditEnabled}
                drawLineMode={contributionDrawLineMode}
                tracePoints={tracePoints}
                traceRingMode={contributionTraceRingMode}
                traceCaptureMode={traceCaptureMode}
                traceVertexEditEnabled={traceVertexEditEnabled}
                selectedTraceVertexIndex={selectedTraceVertexIndex}
                onTraceVertexSelect={setSelectedTraceVertexIndex}
                trafficSignalsEnabled={layerSettings.trafficSignals}
                layerSettings={layerSettings}
                onCampaignZoneClick={
                    urlCampaignId && !fieldSessionActive ? selectCampaignZone : undefined
                }
                onContributionClick={(location) => {
                    if (import.meta.env.DEV) {
                        console.info('[coordal:contribution] pin set', location);
                    }
                    if (isFindPostcodeMapClick) {
                        setFindPostcodeLocation(location);
                        mapRef.current?.placeContributionMarker(location.lat, location.lng);
                        return;
                    }
                    setContributionLocation(location);
                }}
                onDrawComplete={handleContributionDrawComplete}
                onDrawGeometryChange={handleContributionDrawGeometryChange}
                onMapClick={() => {
                    setSheetState('minimized');
                    useMapStore.getState().setShowSearchResults(false);
                    setControlsExpanded(false);
                }}
            />

            {urlCampaignId && !isHeroMode && isAuthenticated && (
                <CampaignWorkspacePanel
                    campaignId={urlCampaignId}
                    mapRef={mapRef}
                    isMapLoaded={isMapLoaded}
                    isAuthenticated={isAuthenticated}
                    focusZoneId={urlZoneId}
                    onClearFocusedZone={clearCampaignZone}
                    onSelectCampaignZone={selectCampaignZone}
                    onFieldSessionActiveChange={setFieldSessionActive}
                    onFieldCaptureLiveChange={setFieldCaptureLive}
                    compactDock
                    homeOverlayMode={campaignHomeOverlay}
                    onGoHome={goToCampaignHome}
                    onReturnToWorkspace={returnFromCampaignHome}
                    onOpenContribution={handleOpenContributionFromCampaign}
                    hideSessionDock={showAddLocation}
                    onExitCampaign={exitCampaignMode}
                    userLocation={userLocation
                        ? {
                            lat: userLocation.lat,
                            lng: userLocation.lng,
                            accuracyM: userLocation.accuracyM,
                        }
                        : null}
                    onRequireAuth={() => openAuthModal('login')}
                    onOpenProfile={() =>
                        isAuthenticated ? openProfile() : openAuthModal('login')}
                    onOpenSettings={() => openSettings()}
                    onRequestLocationPrompt={openLocationPrompt}
                    onBrowseFieldCampaigns={exitCampaignAndOpenAssignmentsPicker}
                    collapsed={campaignPanelCollapsed}
                    onToggleCollapse={() => setCampaignPanelCollapsed((c) => !c)}
                />
            )}

            {!isHeroMode && (
                <MyCampaignsLauncher
                    isAuthenticated={isAuthenticated}
                    isHeroMode={isHeroMode}
                    suppressed={!!urlCampaignId}
                    assignmentsEligible={showFieldAssignmentsControl}
                    expandNonce={myCampaignsExpandNonce}
                    listOpenOnlyNonce={myCampaignsListOpenOnlyNonce}
                    exploreChromeHidden={fieldCampaignsChromeSuppressed}
                    anchorAboveBottomSheet={!fieldCampaignsChromeSuppressed}
                    onEngageFieldCampaigns={engageFieldCampaignsChrome}
                    onRequestExitFieldCampaigns={() => setConfirmExitFieldCampaigns(true)}
                    listCollapseKey={fieldLauncherCollapseKey}
                    onOpenCampaign={openCampaignFromUrl}
                />
            )}

            {/* Coordal Maps attribution — bottom-left (also GPS pill during field mapping in campaign) */}
            {isMapLoaded && !isHeroMode && !showDirections && !isNavigating && (
                <>
                    {showExploreMapChrome ? (
                        <MapTopRightOverlays
                            layers={layerSettings}
                            isWeatherView={isWeatherView}
                            forecastDays={forecastDays}
                            forecastLoading={forecastLoading}
                            forecastVariable={forecastVariable}
                            districtName={forecastDistrictName}
                            onForecastVariableChange={setForecastVariable}
                            onCloseWeatherView={closeWeatherView}
                            liveLocationActive={liveForecastLocation.source === 'live'}
                            unavailableMessage={weatherUnavailableMessage}
                        />
                    ) : null}
                    {(showExploreMapChrome || showFieldMappingGpsPill || showAddLocation) ? (
                        <MapAttribution
                            zoom={showExploreMapChrome || showAddLocation ? mapZoom : undefined}
                            center={mapCenter}
                            showFieldMappingGpsPill={showFieldMappingGpsPill}
                            fieldMappingGpsAccuracyM={fieldMappingGpsAccuracyM}
                            fieldMappingGpsSkippedCount={fieldMappingGpsSkippedCount}
                        />
                    ) : null}
                </>
            )}

            {isWeatherView && isMapLoaded && !isHeroMode && (
                <>
                    <WeatherSummaryModal
                        open={showForecastSummary}
                        summary={forecastSummaryText}
                        loading={forecastLoading && forecastDays.length === 0}
                        days={forecastDays}
                        districtName={forecastDistrictName}
                        postcode={forecastPostcode}
                        unavailableMessage={weatherUnavailableMessage}
                        onDismiss={() => {
                            setShowForecastSummary(false);
                            setWeatherOutlookCollapsed(false);
                        }}
                        onSubscribe={() => {
                            setShowForecastSummary(false);
                            setWeatherOutlookCollapsed(false);
                            setShowWeatherSubscribe(true);
                        }}
                        isSubscribed={isWeatherSubscribed}
                    />
                    <div className="pointer-events-none fixed inset-x-0 bottom-0 z-[56] mx-auto max-w-3xl px-0 sm:px-2">
                        <WeatherOutlookPanel
                            days={forecastDays}
                            hours={forecastHours}
                            loading={forecastLoading}
                            activeVariable={forecastVariable}
                            selectedPeriod={forecastPeriod}
                            availablePeriods={forecastAvailablePeriods}
                            districtName={forecastDistrictName}
                            unavailableMessage={weatherUnavailableMessage}
                            summaryHidden={!showForecastSummary}
                            onOpenSummary={() => {
                                setShowForecastSummary(true);
                                setWeatherOutlookCollapsed(true);
                            }}
                            onSubscribe={() => setShowWeatherSubscribe(true)}
                            isSubscribed={isWeatherSubscribed}
                            onVariableChange={setForecastVariable}
                            onPeriodChange={setForecastPeriod}
                            climateActiveLayer={climateActiveLayer}
                            climateSelectedMonths={climateSelectedMonths}
                            onClimateLayerSelect={handleWeatherClimateLayerSelect}
                            onClimateMonthToggle={handleWeatherClimateMonthToggle}
                            collapsed={weatherOutlookCollapsed}
                            onCollapsedChange={setWeatherOutlookCollapsed}
                        />
                    </div>
                </>
            )}

            {/* Loading overlay — fixed so it covers bottom sheet + entire viewport */}
            {!isMapLoaded && <MapSplashLoader variant="full" />}

            {/* Auth Modal - hide in hero mode */}
            {!isHeroMode && <AuthModal />}

            {!isHeroMode && (
                <WeatherSubscribeModal
                    open={showWeatherSubscribe}
                    onClose={() => setShowWeatherSubscribe(false)}
                    lat={isWeatherView ? liveForecastLocation.lat : briefingLat}
                    lng={isWeatherView ? liveForecastLocation.lng : briefingLng}
                    location={isWeatherView ? forecastLocation : briefingLocation}
                    onSubscribed={(email) => {
                        if (isWeatherView) {
                            markWeatherSubscribed(email);
                            return;
                        }
                        markBriefingSubscribed(email);
                        markWeatherDailyBriefingDismissed(briefingLat, briefingLng);
                        setShowDailyBriefing(false);
                    }}
                />
            )}

            {!isHeroMode && !isWeatherView && !urlCampaignId && canShowUserWeatherForecast && (
                <WeatherDailyBriefing
                    open={showDailyBriefing}
                    loading={briefingLoading && briefingDays.length === 0}
                    summary={briefingSummaryText}
                    today={briefingDays[0] ?? null}
                    districtName={briefingDistrictName}
                    postcode={briefingPostcode}
                    isSubscribed={isBriefingSubscribed}
                    onDismiss={dismissDailyBriefing}
                    onSubscribe={handleDailyBriefingSubscribe}
                    onOpenFullForecast={handleOpenFullForecastFromBriefing}
                />
            )}

            {/* Profile Panel - hide in hero mode */}
            {!isHeroMode && (
                <ProfilePanel
                    isOpen={showProfile}
                    onClose={() => setShowProfile(false)}
                    onOpenSettings={() => {
                        setShowProfile(false);
                        setPanelReturnTo('profile');
                        setSettingsInitialSection(undefined);
                        setShowSettings(true);
                    }}
                    onBrowseFieldCampaigns={
                        urlCampaignId || !fieldCampaignsMenuState
                            ? undefined
                            : () => {
                                setShowProfile(false);
                                if (!fieldCampaignsMenuState.disabled) requestOpenMyCampaignsPicker();
                            }
                    }
                    fieldCampaignsDisabled={urlCampaignId ? undefined : fieldCampaignsMenuState?.disabled}
                    fieldCampaignsSubtitle={urlCampaignId ? undefined : fieldCampaignsMenuState?.subtitle}
                    onOpenContributions={() => {
                        setShowProfile(false);
                        setPanelReturnTo('profile');
                        setShowMyContributions(true);
                    }}
                    onOpenSavedPlaces={() => {
                        setShowProfile(false);
                        setPanelReturnTo('profile');
                        setShowSavedPlaces(true);
                    }}
                    onOpenFindPostcode={() => openFindPostcode('profile')}
                    onOpenRecentActivity={() => {
                        setShowProfile(false);
                        setPanelReturnTo('profile');
                        setShowRecentActivity(true);
                    }}
                    onOpenWeatherForecast={() => {
                        setShowProfile(false);
                        openWeatherView();
                    }}
                />
            )}

            {/* Settings Panel - hide in hero mode */}
            {!isHeroMode && (
                <SettingsPanel
                    isOpen={showSettings}
                    onClose={() => {
                        handlePanelNavigateBack(() => {
                            setShowSettings(false);
                            setSettingsInitialSection(undefined);
                        });
                    }}
                    initialSection={settingsInitialSection}
                    currentMapStyle={mapStyle}
                    onMapStyleChange={handleStyleChange}
                    onOpenLayerSettings={() => { setShowSettings(false); setShowLayerSettings(true); }}
                    onOpenWeatherForecast={() => {
                        setShowSettings(false);
                        setSettingsInitialSection(undefined);
                        openWeatherView();
                    }}
                    onToggle3D={(enabled) => handleToggleLayer('buildings3D', enabled)}
                    is3DEnabled={layerSettings.buildings3D}
                    onBrowseFieldCampaigns={
                        urlCampaignId || !fieldCampaignsMenuState
                            ? undefined
                            : () => {
                                setShowSettings(false);
                                setSettingsInitialSection(undefined);
                                if (!fieldCampaignsMenuState.disabled) requestOpenMyCampaignsPicker();
                            }
                    }
                    fieldCampaignsDisabled={urlCampaignId ? undefined : fieldCampaignsMenuState?.disabled}
                    fieldCampaignsSubtitle={urlCampaignId ? undefined : fieldCampaignsMenuState?.subtitle}
                    onOpenFindPostcode={() => openFindPostcode('settings')}
                />
            )}

            {!isHeroMode && (
                <FindPostcodePanel
                    isOpen={showFindPostcode}
                    onClose={closeFindPostcode}
                    onNavigateBack={handleFindPostcodeNavigateBack}
                    restoreSnapshot={findPostcodeSnapshot}
                    onRestoreConsumed={() => setFindPostcodeSnapshot(null)}
                    onEnableMapClick={handleEnableFindPostcodeMapClick}
                    selectedLocation={findPostcodeLocation}
                    onSetLocation={setFindPostcodeLocation}
                    onFlyTo={(center, zoom) => mapRef.current?.flyTo(center, zoom)}
                    onPlaceMarker={(lat, lng) => mapRef.current?.placeContributionMarker(lat, lng)}
                    onRemoveMarker={() => mapRef.current?.removeContributionMarker()}
                    onOpenPlace={(placeId) => {
                        void selectPlace(placeId);
                        setSheetState('half');
                    }}
                    onSuggestEdit={(locationResult) => {
                        requireAuth(() => {
                            if (!locationResult.place_id?.startsWith('loc_')) return;
                            setFindPostcodeSnapshot({
                                step: 'result',
                                location: findPostcodeLocation,
                                result: locationResult,
                            });
                            setShowFindPostcode(false);
                            setIsFindPostcodeMapClick(false);
                            mapRef.current?.removeContributionMarker();
                            setSuggestEditPlace({
                                id: locationResult.place_id,
                                name: '',
                                category: 'place',
                                address: locationResult.address,
                                coordinates: {
                                    lat: locationResult.coordinates.latitude,
                                    lng: locationResult.coordinates.longitude,
                                },
                                ghanaPostCode: locationResult.postcode,
                                ghanaPostGPS: locationResult.ghana_post_gps,
                                district: locationResult.district,
                                region: locationResult.region,
                                placeType: 'building',
                            });
                            setShowAddLocation(true);
                        });
                    }}
                />
            )}

            {/* Add Place Modal - hide in hero mode */}
            {!isHeroMode && (
                <AddPlaceModal
                    isOpen={showAddPlace}
                    onClose={() => {
                        const returnTo = panelReturnTo;
                        setShowAddPlace(false);
                        setAddPlacePrefill(null);
                        setPanelReturnTo(null);
                        navigateToPanelReturn(returnTo);
                    }}
                    defaultLabel={addPlaceLabel}
                    prefillName={addPlacePrefill?.name}
                    prefillAddress={addPlacePrefill?.address}
                    prefillCoords={addPlacePrefill?.coords}
                    prefillPostcode={addPlacePrefill?.postcode}
                    prefillPlaceId={addPlacePrefill?.placeId}
                />
            )}

            {/* Saved Places Panel - hide in hero mode */}
            {!isHeroMode && (
                <SavedPlacesPanel
                    isOpen={showSavedPlaces}
                    onClose={() => {
                        setPanelReturnTo(null);
                        setShowSavedPlaces(false);
                    }}
                    onNavigateBack={() => handlePanelNavigateBack(() => setShowSavedPlaces(false))}
                    onFindPostcode={() => openFindPostcode('savedPlaces')}
                    onAddPlace={(label) => {
                        setAddPlaceLabel(label);
                        setAddPlacePrefill(null);
                        setPanelReturnTo('savedPlaces');
                        setShowAddPlace(true);
                        setShowSavedPlaces(false);
                    }}
                />
            )}

            {/* Recent Activity Panel - hide in hero mode */}
            {!isHeroMode && (
                <RecentActivityPanel
                    isOpen={showRecentActivity}
                    onClose={() => {
                        setPanelReturnTo(null);
                        setShowRecentActivity(false);
                    }}
                    onNavigateBack={() => handlePanelNavigateBack(() => setShowRecentActivity(false))}
                />
            )}

            {/* My Contributions Panel - hide in hero mode */}
            {!isHeroMode && (
                <MyContributionsPanel
                    isOpen={showMyContributions}
                    onClose={() => {
                        setPanelReturnTo(null);
                        setShowMyContributions(false);
                    }}
                    onNavigateBack={() => handlePanelNavigateBack(() => setShowMyContributions(false))}
                    onViewOnMap={handleViewContributionOnMap}
                    onEditContribution={handleEditContribution}
                />
            )}


            {/* Add Location Panel - hide in hero mode */}
            {!isHeroMode && (
                <AnimatePresence>
                    {showAddLocation && (
                        <AddLocationPanel
                            isOpen={showAddLocation}
                            onClose={handleCloseAddLocation}
                            editContribution={editingContribution}
                            suggestEditForPlace={suggestEditPlace}
                            onDismissSuggestEdit={dismissSuggestEditToFindPostcode}
                            campaignId={fieldCaptureLive ? urlCampaignId : null}
                            zoneId={fieldCaptureLive ? urlZoneId : null}
                            campaignFieldCapture={campaignFieldCapture && fieldCaptureLive}
                            onEnableMapClick={handleEnableMapClick}
                            onEnableDrawing={handleEnableDrawing}
                            selectedLocation={contributionLocation}
                            drawnGeometry={drawnGeometry}
                            isTracing={isTracing}
                            tracePoints={tracePoints}
                            onStartTrace={handleStartTrace}
                            onStopTrace={handleStopTrace}
                            onUndoTracePoint={handleUndoTracePoint}
                            onUndoFirstTracePoint={handleUndoFirstTracePoint}
                            onHydrateMarkingGeometry={handleHydrateMarkingGeometry}
                            onClearGeometry={handleClearGeometry}
                            traceDraftFeatureTypeId={traceDraftMeta?.featureTypeId ?? null}
                            onTraceContextChange={handleTraceContextChange}
                            tracePostProcessSummary={tracePostProcessSummary}
                            traceSamplingMode={traceSamplingMode}
                            traceMinDistanceM={traceMinDistanceM}
                            onTraceSamplingModeChange={setTraceSamplingMode}
                            onTraceMinDistanceMChange={setTraceMinDistanceM}
                            traceResampleOnStop={traceResampleOnStop}
                            onTraceResampleOnStopChange={setTraceResampleOnStop}
                            traceCaptureMode={traceCaptureMode}
                            onTraceCaptureModeChange={setTraceCaptureMode}
                            onAddTraceCorner={handleAddTraceCorner}
                            isAddingTraceCorner={isAddingTraceCorner}
                            onThinTrace={handleThinTrace}
                            onResampleFinerTrace={handleResampleFinerTrace}
                            traceFinerSpacingM={pickFinerSpacingM(traceMinDistanceM)}
                            traceThinSpacingM={pickThinSpacingM(traceMinDistanceM)}
                            tracePathModified={tracePathModified}
                            onResetTracePath={handleResetTracePath}
                            canResetTracePath={canResetTracePath}
                            onRestoreRawTrace={handleRestoreRawTrace}
                            canRestoreRawTrace={canRestoreRawTrace}
                            selectedTraceVertexIndex={selectedTraceVertexIndex}
                            onDeleteSelectedTraceVertex={handleDeleteSelectedTraceVertex}
                            onTraceVertexEditActiveChange={setTraceVertexEditEnabled}
                            onTraceEditUndo={handleTraceEditUndo}
                            onTraceEditRedo={handleTraceEditRedo}
                            traceEditCanUndo={traceEditCanUndo}
                            traceEditCanRedo={traceEditCanRedo}
                            onPreviewGeometry={handlePreviewContributionGeometry}
                            onPreviewGuideGeometries={handlePreviewContributionGuideGeometries}
                            onTraceRingModeChange={setContributionTraceRingMode}
                            onDrawTrash={() => mapRef.current?.drawTrash() ?? false}
                            onDrawUndo={() => mapRef.current?.drawUndo() ?? false}
                            onDrawUndoFirst={() => mapRef.current?.drawUndoFirst() ?? false}
                            onSetLocation={(loc) => setContributionLocation(loc)}
                            onFlyTo={(center, zoom) => mapRef.current?.flyTo(center, zoom)}
                            onPlaceMarker={(lat, lng) => mapRef.current?.placeContributionMarker(lat, lng)}
                            onLockZoom={(zoom) => mapRef.current?.lockZoom(zoom)}
                            onUnlockZoom={() => mapRef.current?.unlockZoom()}
                            onHighlightGeometry={(geojson) => mapRef.current?.highlightGeometry(geojson)}
                            onClearHighlight={handleClearMapHighlight}
                            userLocation={userLocation}
                            onUseMyLocation={handleContributionUseMyLocation}
                            onSetDrawnGeometry={setDrawnGeometry}
                            roadSnappingEnabled={roadSnappingEnabled}
                            onRoadSnappingEnabledChange={setRoadSnappingEnabled}
                            snapStatusMessage={snapStatusMessage}
                            roadSnapConnections={roadSnapConnections}
                            onDrawEnterVertexEdit={() => mapRef.current?.drawEnterVertexEdit() ?? false}
                            onRemoveTraceOutliers={handleRemoveTraceOutliers}
                            onStraightenTrace={handleStraightenTrace}
                            onSnapEndpointsNow={handleSnapEndpointsNow}
                            onMarkingDrawFabActive={setMarkingDrawFabActive}
                            onPendingSurveyRefresh={() => setPendingSurveyRefreshNonce((n) => n + 1)}
                        />
                    )}
                </AnimatePresence>
            )}

            {/* Left Controls - Expandable FAB - hide in hero mode and in campaign field view (campaign-only chrome). */}
            {/* Explore top chrome: search row + menu row (see SearchBar controlsRow below). */}

            {/* Directions Panel - hide in hero mode */}
            {!isHeroMode && (
                <AnimatePresence>
                    {showDirections && (
                        <DirectionsPanel
                            originName={userLocation ? 'Your location' : ''}
                            originCoords={userLocation || undefined}
                            destinationName={stopNavDestination?.name ?? selectedPlace?.name}
                            destinationCoords={stopNavDestination?.coords ?? selectedPlace?.coordinates}
                            onClose={() => {
                                useDirectionsStore.getState().clearDirections();
                                setShowDirections(false);
                                setStopNavDestination(null);
                            }}
                            onStartNavigation={handleStartNavigation}
                        />
                    )}
                </AnimatePresence>
            )}

            {/* Drawing Controls — above bottom sheet (z-60) */}
            {(isDrawingEnabled || markingDrawFabActive || traceVertexEditEnabled) && showAddLocation && (
                <div className="fixed top-20 right-3 z-[70] flex flex-col gap-2">
                    <button
                        type="button"
                        onClick={() => {
                            if (tracePathRefineEditing) {
                                handleTraceEditUndo();
                                handlePreviewContributionGeometry(null);
                                return;
                            }
                            if (traceVertexEditEnabled || (markingDrawFabActive && !isDrawingEnabled)) {
                                handleUndoTracePoint();
                                handlePreviewContributionGeometry(null);
                                return;
                            }
                            const hasGeometry = mapRef.current?.drawUndo();
                            handlePreviewContributionGeometry(null);
                            if (!hasGeometry) handleClearGeometry();
                        }}
                        disabled={tracePathRefineEditing && !traceEditCanUndo}
                        className="w-11 h-11 flex items-center justify-center rounded-xl bg-white dark:bg-gray-900/95 dark:backdrop-blur-xl shadow-lg dark:shadow-black/40 dark:ring-1 dark:ring-gray-700/50 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800/80 active:scale-95 transition-all disabled:opacity-40 disabled:cursor-not-allowed"
                        title={tracePathRefineEditing ? 'Undo last change' : 'Undo last point'}
                    >
                        <Undo2 size={20} />
                    </button>
                    <button
                        type="button"
                        onClick={() => {
                            if (tracePathRefineEditing) {
                                handleTraceEditRedo();
                                handlePreviewContributionGeometry(null);
                                return;
                            }
                            if (traceVertexEditEnabled || (markingDrawFabActive && !isDrawingEnabled)) {
                                handleUndoFirstTracePoint();
                                handlePreviewContributionGeometry(null);
                                return;
                            }
                            const hasGeometry = mapRef.current?.drawUndoFirst();
                            handlePreviewContributionGeometry(null);
                            if (!hasGeometry) handleClearGeometry();
                        }}
                        disabled={tracePathRefineEditing && !traceEditCanRedo}
                        className="w-11 h-11 flex items-center justify-center rounded-xl bg-white dark:bg-gray-900/95 dark:backdrop-blur-xl shadow-lg dark:shadow-black/40 dark:ring-1 dark:ring-gray-700/50 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800/80 active:scale-95 transition-all disabled:opacity-40 disabled:cursor-not-allowed"
                        title={tracePathRefineEditing ? 'Redo change' : 'Undo first point'}
                    >
                        {tracePathRefineEditing ? (
                            <Redo2 size={20} />
                        ) : (
                            <Undo2 size={20} className="scale-x-[-1]" />
                        )}
                    </button>
                    {traceVertexEditEnabled ? (
                        <button
                            type="button"
                            onClick={() => {
                                handleDeleteSelectedTraceVertex();
                                handlePreviewContributionGeometry(null);
                            }}
                            disabled={selectedTraceVertexIndex == null}
                            className="w-11 h-11 flex items-center justify-center rounded-xl bg-white dark:bg-gray-900/95 dark:backdrop-blur-xl shadow-lg dark:shadow-black/40 dark:ring-1 dark:ring-gray-700/50 text-red-500 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 active:scale-95 transition-all disabled:opacity-40 disabled:cursor-not-allowed"
                            title={
                                selectedTraceVertexIndex != null
                                    ? `Delete point ${selectedTraceVertexIndex + 1}`
                                    : 'Tap a point on the map, then delete'
                            }
                        >
                            <MapPinOff size={20} />
                        </button>
                    ) : null}
                    <button
                        type="button"
                        onClick={() => {
                            if (traceVertexEditEnabled) {
                                handleClearGeometry();
                                handlePreviewContributionGeometry(null);
                                return;
                            }
                            const hasGeometry = mapRef.current?.drawTrash();
                            handlePreviewContributionGeometry(null);
                            if (!hasGeometry) handleClearGeometry();
                        }}
                        className="w-11 h-11 flex items-center justify-center rounded-xl bg-white dark:bg-gray-900/95 dark:backdrop-blur-xl shadow-lg dark:shadow-black/40 dark:ring-1 dark:ring-gray-700/50 text-red-500 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 active:scale-95 transition-all"
                        title={traceVertexEditEnabled ? 'Clear entire path' : 'Clear drawing'}
                    >
                        <Trash2 size={20} />
                    </button>
                </div>
            )}

            {/* Search Bar - hide when showing directions or in hero mode; keep visible during contributions for navigation */}
            {!showDirections && !isHeroMode && showExploreMapChrome && !fieldCampaignsChromeSuppressed && (
                <SearchBar
                    onGetDirections={handleGetDirections}
                    onNavigateToStop={handleNavigateToStop}
                    onAddMissingPlace={handleOpenAddLocation}
                    controlsRow={
                        <div data-tour-target="left-menu" className="flex flex-col items-start gap-2 min-w-0">
                            <button
                                type="button"
                                data-tour-target="profile"
                                onClick={() => (isAuthenticated ? openProfile() : openAuthModal('login'))}
                                className="w-11 h-11 md:w-10 md:h-10 shrink-0 rounded-xl bg-white dark:bg-gray-900/95 dark:backdrop-blur-xl dark:ring-1 dark:ring-gray-700/50 shadow-lg dark:shadow-black/40 flex items-center justify-center text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800/80 transition-colors"
                                aria-label="Profile"
                                title="Profile"
                            >
                                {isAuthenticated && user ? (
                                    <div className="w-8 h-8 bg-primary-100 dark:bg-primary-900/30 rounded-full flex items-center justify-center">
                                        <span className="text-xs font-bold text-primary-600 dark:text-primary-400">
                                            {user.name
                                                ? (user.name.split(' ').length > 1
                                                    ? user.name.split(' ')[0][0] + user.name.split(' ').slice(-1)[0][0]
                                                    : user.name[0]).toUpperCase()
                                                : '?'}
                                        </span>
                                    </div>
                                ) : (
                                    <User size={20} />
                                )}
                            </button>
                            <ExpandableFAB
                                position="left"
                                expandDirection="down"
                                isExpanded={controlsExpanded}
                                onActionClick={() => setControlsExpanded(false)}
                                actions={[
                                    ...(showFieldAssignmentsControl
                                        ? [
                                            {
                                                id: 'field-campaigns',
                                                icon: (
                                                    <MapPinned size={20} className="text-white" strokeWidth={2.25} />
                                                ),
                                                label: 'My field campaigns',
                                                onClick: () => {
                                                    setControlsExpanded(true);
                                                    setMyCampaignsListOpenOnlyNonce((n) => n + 1);
                                                },
                                                color: 'text-white',
                                                bgColor:
                                                    'bg-gradient-to-br from-cyan-500 to-emerald-600 hover:brightness-110',
                                            },
                                        ]
                                        : []),
                                    {
                                        id: 'appearance',
                                        icon: <Palette size={20} />,
                                        label: 'Appearance',
                                        onClick: () => openSettings('appearance'),
                                    },
                                    {
                                        id: 'weather-forecast',
                                        icon: <CloudSun size={20} />,
                                        label: 'Weather Forecast',
                                        onClick: openWeatherView,
                                    },
                                    {
                                        id: 'settings',
                                        icon: <Settings size={20} />,
                                        label: 'Settings',
                                        onClick: () => openSettings(),
                                    },
                                    {
                                        id: 'directions',
                                        icon: <Navigation size={20} />,
                                        label: 'Directions',
                                        onClick: () => requireAuth(() => setShowDirections(true)),
                                    },
                                    {
                                        id: 'add-location',
                                        icon: <Plus size={20} />,
                                        label: 'Contribute',
                                        onClick: handleOpenAddLocation,
                                        color: 'text-white',
                                        bgColor: 'bg-primary-500 hover:bg-primary-600',
                                    },
                                ]}
                            />
                        </div>
                    }
                />
            )}

            {/* Right map controls — keep expand/zoom/locate during geometry capture; hide in directions/navigation. */}
            {!isHeroMode && !showDirections && !isNavigating
                && ((showExploreMapChrome && !fieldCampaignsChromeSuppressed) || showAddLocation) && (
                <MapControlsFAB
                    compactBottom={false}
                    extraBottomOffset={
                        urlCampaignId && campaignHomeOverlay ? '6.25rem' : undefined
                    }
                    onZoomIn={() => mapRef.current?.zoomIn()}
                    onZoomOut={() => mapRef.current?.zoomOut()}
                    onToggleTilt={() => {
                        const next = nextPitchStep(mapPitch);
                        mapRef.current?.setPitch(next);
                    }}
                    isTilted={mapPitch > 10}
                    bearing={mapBearing}
                    is3DEnabled={layerSettings.buildings3D}
                    onToggle3D={(enabled) => handleToggleLayer('buildings3D', enabled)}
                    isExpanded={controlsExpanded}
                    onToggleExpand={() => {
                        const next = !controlsExpanded;
                        setControlsExpanded(next);
                        if (next) {
                            setSheetState('minimized');
                        }
                    }}
                />
            )}

            {/* Layer Settings Panel - hide in hero mode */}
            {!isHeroMode && (
                <AnimatePresence>
                    {showLayerSettings && (
                        <LayerSettingsPanel
                            isOpen={showLayerSettings}
                            onClose={() => {
                                setShowLayerSettings(false);
                                setLayerSettingsInitialSection(null);
                            }}
                            initialSection={layerSettingsInitialSection}
                            layers={layerSettings}
                            onToggleLayer={handleToggleLayer}
                            onToggleFloodYear={handleToggleFloodYear}
                            onObservedWeatherPeriodChange={handleObservedWeatherPeriodChange}
                            onForecastWeatherPeriodChange={handleForecastWeatherPeriodChange}
                            onOpenWeatherView={openWeatherView}
                            weatherLocationUnavailableMessage={weatherUnavailableMessage}
                            terrainExaggeration={settings.terrainExaggeration}
                            onTerrainExaggerationChange={(value) => {
                                updateSetting('terrainExaggeration', value);
                                mapRef.current?.setTerrainExaggeration(value);
                            }}
                            isTerrainEnabled={isTerrainEnabled}
                            onToggleTerrain={handleToggleTerrain}
                        />
                    )}
                </AnimatePresence>
            )}

            {/* Bottom Sheet - hidden in full campaign workspace; available on home overlay */}
            {showExploreMapChrome &&
                !fieldCampaignsChromeSuppressed &&
                !showDirections &&
                !showAddLocation &&
                !showFindPostcode &&
                !isWeatherView &&
                !isHeroMode && (
                    <BottomSheet>
                        {selectedPlace ? (
                            <PlaceDetails
                                contributionPreview={viewingContribution}
                                onDismissContributionPreview={
                                    viewingContribution ? closeContributionPreview : undefined
                                }
                                onGetDirections={handleGetDirections}
                                onSavePlace={handleSavePlace}
                                onSuggestEdit={
                                    viewingContribution
                                        ? undefined
                                        : () => {
                                            setSuggestEditPlace(selectedPlace);
                                            setShowAddLocation(true);
                                        }
                                }
                            />
                        ) : (
                            <HomeContent
                                onGetDirections={handleGetDirections}
                                onAddPlace={(label) => {
                                    setAddPlaceLabel(label);
                                    setAddPlacePrefill(null);
                                    setShowAddPlace(true);
                                }}
                                onAddMissingPlace={handleOpenAddLocation}
                                onNavigateToStop={handleNavigateToStop}
                            />
                        )}
                    </BottomSheet>
                )}

            {!isHeroMode && (
                <ContributionNotificationToast
                    notification={contributionNotification}
                    onDismiss={dismissContributionNotification}
                    onOpen={openContributionNotification}
                />
            )}

            {/* PWA Install Prompt - hide in hero mode; z-[56] above map controls FAB (z-[55]) */}
            {!isHeroMode && <InstallPrompt />}

            {/* Product Tour */}
            {!isHeroMode && (
                <ProductTour
                    onExpandControls={() => setControlsExpanded(true)}
                    onCollapseControls={() => setControlsExpanded(false)}
                    onExpandLeftMenu={() => {
                        setControlsExpanded(true);
                        setSheetState('minimized');
                    }}
                    onCollapseLeftMenu={() => setControlsExpanded(false)}
                    onShowBottomSheet={() => setSheetState('collapsed')}
                    onShowBottomSheetHalf={() => {
                        setControlsExpanded(false);
                        setSheetState('half');
                    }}
                    onHideBottomSheet={() => setSheetState('minimized')}
                    onExpandCampaignDock={() => setCampaignPanelCollapsed(false)}
                    onCollapseCampaignDock={() => setCampaignPanelCollapsed(true)}
                />
            )}

            {!isHeroMode && (
                <LocationPermissionPrompt
                    open={showLocationPrompt}
                    onDismiss={() => setShowLocationPrompt(false)}
                />
            )}

            {!isHeroMode && (
                <AnimatePresence>
                    {confirmExitFieldCampaigns ? (
                        <motion.div
                            className="fixed inset-0 z-[72] flex items-end sm:items-center justify-center p-4 bg-slate-950/40 backdrop-blur-sm dark:bg-gray-950/55"
                            role="dialog"
                            aria-modal="true"
                            aria-labelledby="field-campaigns-exit-title"
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                            transition={{ duration: 0.18 }}
                            onClick={() => setConfirmExitFieldCampaigns(false)}
                        >
                            <motion.div
                                initial={{ opacity: 0, scale: 0.96, y: 14 }}
                                animate={{ opacity: 1, scale: 1, y: 0 }}
                                exit={{ opacity: 0, scale: 0.98, y: 10 }}
                                transition={{ type: 'spring', damping: 28, stiffness: 360 }}
                                className="w-full max-w-md overflow-hidden rounded-2xl border border-slate-200/75 bg-white/95 shadow-2xl shadow-slate-900/10 backdrop-blur-xl dark:border-gray-600/45 dark:bg-gray-900/94 dark:shadow-black/35"
                                onClick={(e) => e.stopPropagation()}
                            >
                                <div
                                    className="h-1 w-full bg-gradient-to-r from-cyan-500 via-teal-500 to-emerald-500 opacity-90 dark:opacity-80"
                                    aria-hidden
                                />
                                <div className="p-5">
                                    <div className="flex gap-3">
                                        <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-amber-100/95 dark:bg-amber-500/12 ring-1 ring-amber-300/40 dark:ring-amber-400/25">
                                            <AlertTriangle className="h-5 w-5 text-amber-700 dark:text-amber-400" />
                                        </div>
                                        <div>
                                            <h2
                                                id="field-campaigns-exit-title"
                                                className="text-base font-semibold text-slate-900 dark:text-gray-50"
                                            >
                                                Leave field campaigns?
                                            </h2>
                                            <p className="mt-2 text-sm text-slate-600 dark:text-gray-300/95 leading-relaxed">
                                                The map home bar and zoom controls will come back. If you opened a campaign
                                                link in another tab, finish or end your session there first —{' '}
                                                <strong className="font-semibold text-slate-800 dark:text-gray-100">
                                                    active sessions are not ended automatically
                                                </strong>{' '}
                                                from this screen.
                                            </p>
                                        </div>
                                    </div>
                                    <div className="mt-5 flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
                                        <button
                                            type="button"
                                            className="rounded-xl border border-slate-200/95 bg-white/90 px-4 py-2.5 text-sm font-semibold text-slate-800 hover:bg-slate-50/95 dark:border-gray-600/70 dark:bg-gray-800/90 dark:text-gray-100 dark:hover:bg-gray-700/85"
                                            onClick={() => setConfirmExitFieldCampaigns(false)}
                                        >
                                            Cancel
                                        </button>
                                        <button
                                            type="button"
                                            className="rounded-xl bg-gradient-to-r from-cyan-600 to-teal-600 px-4 py-2.5 text-sm font-semibold text-white shadow-md shadow-cyan-900/15 hover:from-cyan-500 hover:to-teal-500 dark:from-cyan-500 dark:to-teal-500 dark:shadow-cyan-950/35 dark:hover:from-cyan-400 dark:hover:to-teal-400"
                                            onClick={confirmLeaveFieldCampaignsSurface}
                                        >
                                            Exit field campaigns
                                        </button>
                                    </div>
                                </div>
                            </motion.div>
                        </motion.div>
                    ) : null}
                </AnimatePresence>
            )}

            {/* Cookie consent removed — lives on marketing site (coordal.com) only */}

            {/* Feedback survey — appears after place lookup or navigation */}
            <AnimatePresence>
                {survey && (
                    <FeedbackSurvey
                        trigger={survey.trigger}
                        placeName={survey.placeName}
                        onDismiss={dismissSurvey}
                        onSubmit={submitSurvey}
                    />
                )}
            </AnimatePresence>
        </div>
    );
}

export default App;
